import React, { useState, useEffect, useRef } from "react";
import { CheckCircle, XCircle, Home, Timer, Users, Navigation, MapPin, ScanFace, RefreshCw, LogOut, Settings, BarChart2, CalendarDays, ClipboardList, PenLine, History, Send, Eye, Check, X, Clock, AlertTriangle, Calendar, Download, Search, ChevronLeft, ChevronRight } from "lucide-react";
import * as faceapi from "@vladmandic/face-api";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Bg, Bt, Cd, Sl, Inp, Av, Tbl, Stat, Sec, KpiStrip, Pr } from "../components";
import { ReminderConfigCard } from "../config/ReminderConfigCard";
import { ShiftsCard } from "../config/ShiftsCard";
import { ActivityLog } from "../components/ActivityLog";
import { ShiftRequestsTab } from "../components/ShiftRequests";
import { ManualAttendanceModal } from "../components/ManualAttendanceModal";
import { fmtTime, getAppTimezone } from "../utils/timezone";
import { notify, promptDialog } from "../notify";
import { fmtDMY } from "../dateFormat";
import { dayOutcome, dayOutcomeText, halfLeaveText, absentOtherHalfText, absentCount, leaveCount } from "../utils/attendanceDay";

// Client-side pre-check bar for the live camera preview. Mirrors the
// server's authoritative threshold (server/services/faceVerify.js,
// FACE_MATCH_MIN_CONFIDENCE) so the UI doesn't show "Matched ✓" for a
// selfie the server is then going to reject.
const FACE_MIN_CONFIDENCE = 52;

// Compact "Before regularization" summary. Snapshot is saved server-side at
// approval time so we can always answer "what did this day look like before
// the manager overwrote it?" — renders nothing for pending/unsnapshotted rows.
const RegSnapshot = ({ snap }) => {
  if (!snap) return null;
  if (snap.had_record === false) {
    return <div className="text-[12px] text-t3 italic mt-[3px]">Before: no attendance record (employee hadn't checked in)</div>;
  }
  return <div className="text-[12px] text-t3 mt-[3px]">
    <span className="font-semibold">Before:</span>{" "}
    {snap.status || "—"}
    {snap.work_hours != null && <span> · {Number(snap.work_hours).toFixed(2)}h</span>}
    {snap.check_in && <span> · in {fmtTime(snap.check_in)}</span>}
    {snap.check_out && <span> · out {fmtTime(snap.check_out)}</span>}
    {snap.is_late && <span className="text-a"> · was late</span>}
  </div>;
};

// Truncated "Reason" table cell that reveals the full text on click — the
// regularization tables clip long reasons with an ellipsis so columns stay
// aligned, but that left no way to actually read a reason once it didn't
// fit. Click opens a small popover with the untruncated text; click
// anywhere outside (capture phase, so a modal's own stopPropagation on
// mousedown can't swallow it — see SearchSelect/MultiSelect) closes it.
const ReasonCell = ({ text, maxW = 180 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown, true); document.removeEventListener("keydown", onEsc); };
  }, [open]);
  if (!text) return <span className="text-[12px] text-t3">—</span>;
  return (
    <div ref={ref} className="relative inline-block">
      <span
        className={`text-[12px] text-t2 block overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer hover:text-p hover:underline`}
        style={{ maxWidth: maxW }}
        onClick={() => setOpen(o => !o)}
      >
        {text}
      </span>
      {open && (
        <div className="absolute z-[70] mt-1 left-0 w-[280px] p-3 bg-bg1 border border-b1 rounded-lg shadow-lg text-[13px] text-t0 leading-[1.5] whitespace-pre-wrap">
          {text}
        </div>
      )}
    </div>
  );
};

// Live-ticking "time since check-in", with a countdown to the minimum
// working hours target. Ticks off its own 1s interval so it stays accurate
// without depending on the page's data-polling cadence.
function LiveDuration({ since, minHours }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsedSec = Math.max(0, Math.floor((now - new Date(since).getTime()) / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  const hh = pad(Math.floor(elapsedSec / 3600)), mm = pad(Math.floor((elapsedSec % 3600) / 60)), ss = pad(elapsedSec % 60);
  const remainingSec = Math.round((Number(minHours) || 0) * 3600) - elapsedSec;
  // Kept deliberately small/muted — "Checked in at HH:MM" above is the
  // thing that matters at a glance; this is a secondary, de-emphasized detail.
  return <div className="mt-2 pt-2 border-t border-b0/60">
    <div className="text-[13px] font-semibold text-t2 font-mono tabular-nums tracking-tight">{hh}:{mm}:{ss} Checked in</div>
    <div className="text-[11px] text-t3 mt-[2px]">
      {remainingSec > 0
        ? <>{pad(Math.floor(remainingSec / 3600))}:{pad(Math.floor((remainingSec % 3600) / 60))}:{pad(remainingSec % 60)} to reach {minHours}h min</>
        : <span className="text-g font-semibold">Minimum hours reached ✓</span>}
    </div>
  </div>;
}

// Live camera face-verification step for check-in/check-out. Extracted
// out of what used to be a render-time IIFE inside AttendancePage: that
// version called `React.createRef()` fresh on every render and kicked
// off the camera via a bare `setTimeout` in the render body itself.
// startCam() fires several setAttCheckState calls while it's still
// running (each one a re-render), so the stream could end up attached
// to a ref that a later render had already detached — the on-screen
// <video> stayed black even though _camReady flipped true. A real
// component with a stable useRef + useEffect avoids that race.
// Attendance record detail — full-day summary with selfies, a check-in/out
// timeline, and (for reviewers) an editable notes field. Hoisted to a
// standalone component + hooked up once per tab (via attRecDetModal below)
// so every place that can open a day — Today's Records table, Team Report
// calendar, My Attendance calendar — shows it, instead of only the tab that
// happened to have its own inline copy.
// Haversine distance in meters — mirrors server/routes/attendance.js so the
// check-out tile (which the server never stores a distance string for) can
// be computed client-side against the same office coordinates.
function haversineM(lat1, lon1, lat2, lon2) {
  const R = 6371000, toRad = d => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function AttendanceDetailModal({ rec, role, officeList, onClose, onPhotoClick, onSaveNotes, can = () => true }) {
  const [notes, setNotes] = useState(rec.notes || "");
  const [saving, setSaving] = useState(false);
  const stC = (s) => s === "Present" ? K.g : s === "WFH" ? K.bl : s === "Late" ? K.a : s === "Half Day" ? K.pu : s === "Absent" ? K.r : s === "Leave" ? K.cy : s === "Leave Pending" ? K.a : s === "Holiday" ? K.tl : s === "Week Off" ? K.t3 : s === "Yet to Check In" ? K.a : K.t3;
  const fmtHM = (h) => { const n = parseFloat(h); if (isNaN(n) || n <= 0) return "—"; const m = Math.round(n * 60); const hh = Math.floor(m / 60), mm = m % 60; return hh === 0 ? `${mm}m` : mm === 0 ? `${hh}h` : `${hh}h ${mm}m`; };
  const tt = (t) => t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: getAppTimezone() }) : "—";
  const sc = stC(rec.status);
  // reg_log grants admin/hr_head/centre_hr edit; bulk_reg grants admin/centre_head edit —
  // combined they cover the same four roles the old array check allowed.
  const canEdit = can('attendance', 'reg_log', null, 'edit') || can('attendance', 'bulk_reg', null, 'edit');
  const hrs = parseFloat(rec.work_hours) || 0;

  // Check-in distance comes pre-formatted from the server (remarks, computed
  // at check-in time). Check-out never gets its own remark, so compute its
  // distance from the same office here, client-side.
  const office = (officeList || []).find(o => o.id === rec.office_id);
  const checkoutDistM = (office?.latitude && office?.longitude && rec.check_out_lat && rec.check_out_lng)
    ? Math.round(haversineM(parseFloat(rec.check_out_lat), parseFloat(rec.check_out_lng), parseFloat(office.latitude), parseFloat(office.longitude)))
    : null;
  const checkoutLocLabel = rec.check_out
    ? (rec.check_out_remarks || (checkoutDistM != null ? `${office?.name || 'Office'} (${checkoutDistM}m away)` : (rec.check_out_lat ? "Location recorded" : "—")))
    : "—";

  return <div className="fixed inset-0 animate-fadeIn bg-black/55 backdrop-blur-[2px] z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg1 rounded-2xl w-full max-w-[768px] max-h-[88vh] overflow-auto shadow-[0_24px_70px_rgba(0,0,0,0.25)] border border-b0" onMouseDown={e => e.stopPropagation()}>
      <div className="relative px-5 pt-5 pb-4" style={{ background: `linear-gradient(135deg, ${sc}16, transparent)` }}>
        <button onClick={onClose} className="absolute top-4 right-4 bg-pg hover:bg-pb border-0 text-p cursor-pointer rounded-full w-8 h-8 flex items-center justify-center transition-colors"><X size={16} /></button>
        <div className="flex items-center gap-3">
          <div className="relative flex-shrink-0">
            {rec.check_in_photo
              ? <img src={uploadUrl(rec.check_in_photo)} alt="check-in selfie" title="Click to enlarge" onClick={() => onPhotoClick("in")} className="w-16 h-16 rounded-full object-cover cursor-pointer" style={{ border: `3px solid ${sc}` }} />
              : <Av nm={rec.employee_name || "?"} sz={64} />}
            {rec.check_out_photo && <img src={uploadUrl(rec.check_out_photo)} alt="check-out selfie" title="Click to enlarge" onClick={() => onPhotoClick("out")} className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full object-cover border-2 cursor-pointer" style={{ borderColor: K.bg1, boxShadow: `0 0 0 2px ${K.bl}` }} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[16px] font-extrabold text-t0 truncate">{rec.employee_name}</div>
            <div className="text-[14px] text-t3 mb-[6px]">{rec.emp_code} · {fmtDMY(rec.date)}</div>
            <div className="flex items-center gap-[6px] flex-wrap">
              <Bg c={sc} dot sm>{rec.status}</Bg>
              <Bg c={rec.location_type === "Office" ? K.g : rec.location_type === "Home" ? K.bl : rec.location_type === "Alt Location" ? K.pu : K.a} sm>{rec.location_type || "Manual"}</Bg>
              {rec.is_late && <Bg c={K.a} sm>{fmtHM(rec.late_minutes / 60)} late</Bg>}
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 pb-5">
        <div className="flex items-center gap-2 bg-pg rounded-[14px] p-[14px] mb-3">
          <div className="flex-1 text-center min-w-0">
            <div className="text-[12px] text-t3 font-semibold tracking-wide mb-[2px]">CHECK IN</div>
            <div className="text-[17px] font-extrabold text-g font-mono">{tt(rec.check_in)}</div>
          </div>
          <div className="flex flex-col items-center px-2 flex-shrink-0">
            <Clock size={14} color={K.t3} />
            <div className="text-[14px] font-bold mt-[2px] whitespace-nowrap" style={{ color: hrs >= 8 ? K.g : hrs > 0 ? K.a : K.t3 }}>{fmtHM(rec.work_hours)}</div>
          </div>
          <div className="flex-1 text-center min-w-0">
            <div className="text-[12px] text-t3 font-semibold tracking-wide mb-[2px]">CHECK OUT</div>
            <div className="text-[17px] font-extrabold text-bl font-mono">{tt(rec.check_out)}</div>
          </div>
        </div>

        <div className="bg-pg rounded-[12px] p-[10px] flex items-center gap-2 min-w-0 mb-2">
          <ScanFace size={14} color={rec.face_verified ? K.g : K.r} className="flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-[12px] text-t3">FACE VERIFIED</div>
            <div className="text-[14px] font-semibold" style={{ color: rec.face_verified ? K.g : K.r }}>{rec.face_verified ? "Matched" : "No"}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-pg rounded-[12px] p-[10px] min-w-0">
            <div className="flex items-center gap-2 mb-[6px]">
              <MapPin size={14} color={K.g} className="flex-shrink-0" />
              <div className="text-[12px] text-t3 font-semibold tracking-wide">CHECK-IN LOCATION</div>
            </div>
            <div className="text-[14px] font-semibold text-t1 truncate">{rec.remarks || "—"}</div>
            {rec.check_in_lat && <div className="text-[12px] text-t3 mt-[3px]">📍 {Number(rec.check_in_lat).toFixed(5)}, {Number(rec.check_in_lng).toFixed(5)}</div>}
          </div>
          <div className="bg-pg rounded-[12px] p-[10px] min-w-0">
            <div className="flex items-center gap-2 mb-[6px]">
              <MapPin size={14} color={K.bl} className="flex-shrink-0" />
              <div className="text-[12px] text-t3 font-semibold tracking-wide">CHECK-OUT LOCATION</div>
            </div>
            <div className="text-[14px] font-semibold text-t1 truncate">{checkoutLocLabel}</div>
            {rec.check_out_lat && <div className="text-[12px] text-t3 mt-[3px]">📍 {Number(rec.check_out_lat).toFixed(5)}, {Number(rec.check_out_lng).toFixed(5)}</div>}
          </div>
        </div>

        {canEdit && rec.id && <div>
          <div className="text-[12px] font-semibold text-t2 mb-[5px] uppercase tracking-wide">Notes</div>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes..." className="w-full min-h-[60px] p-[10px] bg-pg border border-transparent rounded-[12px] text-t0 text-[14px] outline-none resize-y box-border focus:border-p" />
          <Bt s="sm" style={{ marginTop: 8 }} disabled={saving} onClick={async () => { setSaving(true); await onSaveNotes(notes); setSaving(false); }}>{saving ? "Saving..." : "Save Notes"}</Bt>
        </div>}
      </div>
    </div>
  </div>;
}

// Blocks check-in/out until the employee acknowledges every overdue policy
// the server flagged (routes/attendance.js checkin gate, 15 days after
// routes/documents.js bumps a policy's version). Lets them open the file
// and acknowledge right here instead of bouncing to the Policies page.
function PolicyAckGateStep({ attCheckState, setAttCheckState, getGPS }) {
  const [ackingId, setAckingId] = useState(null);
  const policies = attCheckState.pendingPolicies || [];
  const mode = attCheckState._mode === "checkout" ? "checkout" : "checkin";

  const acknowledge = async (id) => {
    setAckingId(id);
    try {
      await api.put(`/api/documents/policies/${id}/acknowledge`);
      const remaining = policies.filter(p => p.id !== id);
      if (remaining.length === 0) {
        notify("All policies acknowledged", { title: "Saved", variant: "success" });
        setAttCheckState(p => ({ ...p, step: "idle", pendingPolicies: [], error: null }));
        getGPS(mode);
      } else {
        setAttCheckState(p => ({ ...p, pendingPolicies: remaining }));
      }
    } catch (e) {
      notify(e.message || "Failed to acknowledge", { title: "Error", variant: "error" });
    } finally { setAckingId(null); }
  };

  return <div>
    <div className="bg-rb rounded-[10px] p-[14px] border border-rd mb-[10px]">
      <div className="flex items-start gap-[8px]">
        <AlertTriangle size={18} color={K.r} className="flex-shrink-0 mt-[1px]" />
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold text-r mb-[3px]">Policy acknowledgment required</div>
          <div className="text-[14px] text-t1 leading-[1.5]">You must open and acknowledge the updated {policies.length > 1 ? "policies" : "policy"} below before you can {mode === "checkout" ? "check out" : "check in"}.</div>
        </div>
      </div>
    </div>
    <div className="flex flex-col gap-[8px] mb-[10px]">
      {policies.map(p => (
        <div key={p.id} className="flex items-center justify-between gap-[8px] p-[10px] rounded-[10px] border border-b1 bg-bg2">
          <div className="text-[14px] font-semibold text-t0 truncate">{p.title}</div>
          <div className="flex gap-[6px] flex-shrink-0">
            {p.file && <Bt v="secondary" s="sm" icon={Eye} onClick={() => window.open(uploadUrl(p.file), "_blank")}>Open</Bt>}
            <Bt v="primary" s="sm" icon={Check} disabled={ackingId === p.id} onClick={() => acknowledge(p.id)}>{ackingId === p.id ? "..." : "Acknowledge"}</Bt>
          </div>
        </div>
      ))}
    </div>
    <Bt v="ghost" full onClick={() => setAttCheckState({ step: "idle", faceVerified: false, coords: null, error: null, loading: false, result: null, pendingPolicies: [] })}>Close</Bt>
  </div>;
}

function FaceCheckStep({ attCheckState, setAttCheckState, myProfile, doCheckIn, doCheckOut }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  // Bumped on every startCam() call (including the extra one React 18
  // StrictMode fires in dev) so a call whose getUserMedia/model-load
  // resolves after a newer call has started can tell it's stale and back
  // off instead of stomping on the newer stream/state.
  const genRef = useRef(0);
  const profilePhoto = myProfile?.basic?.profile_photo || myProfile?.profile_photo || null;

  const startCam = async () => {
    const myGen = ++genRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 320 }, height: { ideal: 240 } } });
      if (myGen !== genRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // play() returns a promise that rejects if srcObject changes again
        // before it resolves (e.g. a newer startCam() call) — that's
        // expected here, not an error, so swallow it rather than let it
        // surface as an unhandled rejection.
        videoRef.current.play().catch(() => { });
      }
      setAttCheckState(p => ({ ...p, _stream: stream, _camReady: true, _faceStatus: "ready" }));
      if (!faceapi.nets.tinyFaceDetector.isLoaded) {
        setAttCheckState(p => ({ ...p, _faceStatus: "loading_models" }));
        await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
        await faceapi.nets.faceLandmark68TinyNet.loadFromUri("/models");
        await faceapi.nets.faceRecognitionNet.loadFromUri("/models");
        if (myGen === genRef.current) setAttCheckState(p => ({ ...p, _faceStatus: "ready" }));
      }
    } catch (e) { if (myGen === genRef.current) setAttCheckState(p => ({ ...p, error: "Camera access denied: " + e.message })); }
  };

  useEffect(() => {
    startCam();
    return () => { genRef.current++; if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } };
  }, []);

  const verifyFace = async () => {
    if (!videoRef.current) return;
    setAttCheckState(p => ({ ...p, _faceStatus: "verifying" }));
    // Capture the frame, then close the camera immediately — it should
    // only be live while the user is framing the shot, not while the
    // (possibly slow) detection/comparison below runs.
    const v = videoRef.current;
    const c = document.createElement("canvas"); c.width = v.videoWidth || 320; c.height = v.videoHeight || 240;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const photo = c.toDataURL("image/jpeg", 0.8);
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setAttCheckState(p => ({ ...p, _stream: null, _camReady: false }));
    try {
      const camDetection = await faceapi.detectSingleFace(c, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 })).withFaceLandmarks(true).withFaceDescriptor();
      if (!camDetection) { setAttCheckState(p => ({ ...p, _faceStatus: "no_face", error: "No face detected. Please look at the camera clearly." })); return; }
      if (!profilePhoto) { setAttCheckState(p => ({ ...p, _faceStatus: "no_profile", error: "No profile photo on record. Contact HR to upload your photo." })); return; }
      const refImg = new Image(); refImg.crossOrigin = "anonymous"; refImg.src = uploadUrl(profilePhoto);
      await new Promise((res, rej) => { refImg.onload = res; refImg.onerror = () => rej(new Error("Failed to load profile photo")); });
      const refDetection = await faceapi.detectSingleFace(refImg, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.3 })).withFaceLandmarks(true).withFaceDescriptor();
      if (!refDetection) { setAttCheckState(p => ({ ...p, _faceStatus: "no_ref_face", error: "Cannot detect face in profile photo. Please contact HR to upload a clearer photo." })); return; }
      const distance = faceapi.euclideanDistance(camDetection.descriptor, refDetection.descriptor);
      const confidence = Math.round(Math.max(0, (1 - distance)) * 100);
      const matched = confidence >= FACE_MIN_CONFIDENCE;
      setAttCheckState(p => ({ ...p, faceVerified: matched, _facePhoto: photo, _faceStatus: matched ? "matched" : "mismatch", _faceConfidence: confidence, _faceDistance: distance.toFixed(3), error: matched ? null : "Face does not match profile photo. Please try again." }));
    } catch (e) { setAttCheckState(p => ({ ...p, _faceStatus: "error", error: "Face verification failed: " + (e.message || "Unknown error") })); }
  };

  const cancelCam = () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); setAttCheckState(p => ({ ...p, step: "idle", _stream: null, _camReady: false, _faceStatus: null })); };
  const retry = () => { setAttCheckState(p => ({ ...p, faceVerified: false, _faceStatus: null, _facePhoto: null, error: null, _camReady: false })); startCam(); };

  const fs = attCheckState._faceStatus;
  // These all mean "we stopped the camera and have nothing to show for
  // it" — render a retry screen instead of a frozen/dead video preview.
  const failStatuses = { mismatch: "Face Does Not Match", no_face: "No Face Detected", no_profile: "No Profile Photo", no_ref_face: "Profile Photo Unreadable", error: "Verification Failed" };
  const isFail = Object.prototype.hasOwnProperty.call(failStatuses, fs);
  return <div>
    <div className="bg-bg2 rounded-xl p-4 text-center mb-3 border-2 border-dashed" style={{ borderColor: attCheckState.faceVerified ? K.g : isFail ? K.r : K.p }}>
      {!attCheckState.faceVerified && !isFail ? <div>
        <div className="text-[14px] font-bold text-t0 mb-1">Face Verification</div>
        <div className="text-[12px] text-t3 mb-2">{fs === "loading_models" ? "Loading face recognition models..." : fs === "verifying" ? "Comparing face with profile photo..." : attCheckState.geoMessage ? `📍 ${attCheckState.geoMessage}${attCheckState.coords?.accuracy ? ` (±${Math.round(attCheckState.coords.accuracy)}m accuracy)` : ""}` : attCheckState.coords ? `📍 ${attCheckState.coords.lat.toFixed(5)}, ${attCheckState.coords.lng.toFixed(5)}${attCheckState.coords.accuracy ? ` (±${Math.round(attCheckState.coords.accuracy)}m accuracy)` : ""}` : "Coordinates captured"}</div>
        <div className="relative rounded-[10px] overflow-hidden mx-auto max-w-[280px] bg-black">
          <video ref={videoRef} autoPlay playsInline muted className="w-full rounded-[10px] block" style={{ transform: "scaleX(-1)" }} />
          <div className="absolute inset-0 rounded-[10px] pointer-events-none border-[3px]" style={{ borderColor: (fs === "verifying" ? K.a : K.p) + "40" }} />
          <div className="absolute top-[15%] left-[20%] right-[20%] bottom-[15%] rounded-full pointer-events-none border-2 border-dashed" style={{ borderColor: K.p + "60" }} />
          {fs === "verifying" && <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-[10px]"><div className="text-white text-[14px] font-semibold">Scanning...</div></div>}
        </div>
        {attCheckState.error && <div className="text-[14px] text-r mt-2 px-[10px] py-[6px] bg-rb rounded-lg">{attCheckState.error}</div>}
        <div className="mt-[10px] flex gap-[6px] justify-center flex-wrap">
          <Bt icon={ScanFace} onClick={verifyFace} disabled={!attCheckState._camReady || fs === "verifying" || fs === "loading_models"}>{fs === "verifying" ? "Verifying..." : fs === "loading_models" ? "Loading..." : "Verify Face"}</Bt>
          <Bt v="ghost" onClick={cancelCam}>Cancel</Bt>
        </div>
        {!profilePhoto && <div className="text-[12px] text-a mt-[6px]">No profile photo found — contact HR to upload your photo first.</div>}
      </div>
        : isFail ? <div>
          <XCircle size={28} color={K.r} className="mx-auto mb-[6px]" />
          <div className="text-[14px] font-bold text-r mb-1">{failStatuses[fs]}</div>
          {attCheckState.error && <div className="text-[14px] text-t2 mb-2">{attCheckState.error}</div>}
          <div className="flex gap-[6px] justify-center">
            <Bt icon={RefreshCw} onClick={retry}>Try Again</Bt>
            <Bt v="ghost" onClick={cancelCam}>Cancel</Bt>
          </div>
        </div>
          : <div>
            <CheckCircle size={28} color={K.g} className="mx-auto mb-[6px]" />
            <div className="text-[14px] font-bold text-g mb-1">Face Matched ✓</div>
            {attCheckState._facePhoto && <img src={attCheckState._facePhoto} alt="face" className="w-20 h-20 rounded-full object-cover mx-auto block border-[3px] border-g" style={{ transform: "scaleX(-1)" }} />}
            <div className="text-[12px] text-t3 mt-[6px]">{attCheckState.geoMessage ? `📍 ${attCheckState.geoMessage}${attCheckState.coords?.accuracy ? ` (±${Math.round(attCheckState.coords.accuracy)}m accuracy)` : ""}` : attCheckState.coords ? `📍 ${attCheckState.coords.lat.toFixed(5)}, ${attCheckState.coords.lng.toFixed(5)}${attCheckState.coords.accuracy ? ` (±${Math.round(attCheckState.coords.accuracy)}m accuracy)` : ""}` : ""}</div>
            {/* The client's own face-api check passed above, but the server
                re-verifies the same photo independently and can still
                reject it (stricter detector, re-encoded image, etc). When
                that happens attCheckState.error is set while faceVerified
                stays true — without this, there was no way back to the
                camera short of a full Cancel. */}
            {attCheckState.error && <Bt v="secondary" s="sm" icon={RefreshCw} onClick={retry} style={{ marginTop: 8 }}>Retake Photo</Bt>}
          </div>}
    </div>
    {attCheckState.faceVerified && <Bt icon={CheckCircle} full onClick={attCheckState._mode === "checkout" ? doCheckOut : doCheckIn} disabled={attCheckState.loading}>{attCheckState.loading ? "Processing..." : (attCheckState._mode === "checkout" ? "Confirm Check Out" : "Confirm Check In")}</Bt>}
  </div>;
}

export const AttendancePage = ({ attData, myProfile, holData = [], role, company, hasReports, onRefresh, can = () => true, hashQuery = {}, clearHashQuery }) => {
  const myEmpIdAtt = myProfile?.basic?.employee_id || myProfile?.employee_id || null;
  // #attendance?tab=reg_approvals&id=N (manager / approver) opens Today's Overview at the approvals card;
  // #attendance?tab=reg_log&id=N (HR) opens the Regularization Requests log.
  const [attTab, setAttTab] = useState(hashQuery?.tab === "shift_requests" ? "shift_requests" : hashQuery?.tab === "reg_log" ? "reg_log" : "today");
  const [regFocusId, setRegFocusId] = useState(hashQuery?.id && (hashQuery?.tab === "reg_approvals" || hashQuery?.tab === "reg_log") ? parseInt(hashQuery.id) : null);
  useEffect(() => {
    if (hashQuery?.tab === "reg_approvals" || hashQuery?.tab === "reg_log") {
      setAttTab(hashQuery.tab === "reg_log" ? "reg_log" : "today");
      if (hashQuery.id) setRegFocusId(parseInt(hashQuery.id));
      if (hashQuery.tab === "reg_approvals") setTimeout(() => { try { document.getElementById("reg-approvals-card")?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch (_) { } }, 600);
    }
  }, [hashQuery?.tab, hashQuery?.id]);
  // Opened from a shift-request notification / email link (#attendance?tab=shift_requests&id=N).
  const [shiftReqOpenId, setShiftReqOpenId] = useState(hashQuery?.tab === "shift_requests" && hashQuery?.id ? parseInt(hashQuery.id) : null);
  useEffect(() => {
    if (hashQuery?.tab === "shift_requests") {
      setAttTab("shift_requests");
      if (hashQuery.id) setShiftReqOpenId(parseInt(hashQuery.id));
    }
  }, [hashQuery?.tab, hashQuery?.id]);
  const [attCheckState, setAttCheckState] = useState({ step: "idle", faceVerified: false, coords: null, error: null, loading: false, result: null });
  const [attRecDet, setAttRecDet] = useState(null);
  // Mark / edit attendance by hand (permission: Today's Overview → Mark / Edit Attendance Manually)
  const [manualAtt, setManualAtt] = useState(null);
  const canManualAtt = can('attendance', 'today', 'manual_attendance', 'edit');
  const [photoView, setPhotoView] = useState(null);
  const [attMonthly, setAttMonthly] = useState([]);
  const [attMonthlySearch, setAttMonthlySearch] = useState("");
  const [attMonthYear, setAttMonthYear] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [teamEmpAtt, setTeamEmpAtt] = useState(null);
  const [attRegForm, setAttRegForm] = useState(null);
  const [attRegNote, setAttRegNote] = useState("");
  const [attSettLocal, setAttSettLocal] = useState(null);
  const [attPendingRegs, setAttPendingRegs] = useState([]);
  const [attGeoFences, setAttGeoFences] = useState([]);
  const [attMyHistory, setAttMyHistory] = useState(null);
  const [attSettings, setAttSettings] = useState(null);
  const [regLog, setRegLog] = useState([]);
  const [regLogFilter, setRegLogFilter] = useState({ status: "", employee_id: "" });
  const [todayFilter, setTodayFilter] = useState({ status: "", office: "", dept: "" });
  const [todaySearch, setTodaySearch] = useState("");
  const [todayPage, setTodayPage] = useState(1);
  const [todayPageSize, setTodayPageSize] = useState(10);
  const [lastSync, setLastSync] = useState(Date.now());
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [regActionBusy, setRegActionBusy] = useState(null); // `${id}:${approve|reject}` while a regularization approval/rejection is in flight
  const [teamViewLoadingId, setTeamViewLoadingId] = useState(null);
  const [regSubmitting, setRegSubmitting] = useState(false);
  const [settSaving, setSettSaving] = useState(false);
  const [geoSavingId, setGeoSavingId] = useState(null);
  const [holOfficeFilter, setHolOfficeFilter] = useState("all"); // admin-only: narrow the Holidays Calendar to one office

  const att = attData || {};
  const sm = att.summary || {};
  const todayRecs = att.today_records || [];
  const officeList = attGeoFences.length ? attGeoFences : (att.offices || []);
  const sett = attSettings || att.settings || { office_start_time: "09:30", office_end_time: "18:30", late_threshold_minutes: 15, minimum_working_hours: 8, half_day_hours: 4, geo_fence_enabled: true, face_verification_enabled: true };
  const myRecs = attMyHistory?.records || [];
  const mySum = attMyHistory?.summary || {};
  const myRegs = attMyHistory?.regularizations || [];

  // Auto-fetch pending regularizations the current user can approve. The
  // backend now filters by manager-of-record (or admin fallback) so anyone
  // who has no reports just gets an empty list.
  useEffect(() => {
    api.get('/api/attendance/regularizations').then(d => setAttPendingRegs(Array.isArray(d) ? d : [])).catch(() => { });
  }, []);

  // Live wall-clock tick for the Self Punch card's digital clock, plus a
  // "synced Xs ago" label that's honest about what it's measuring — the
  // last time `attData` actually changed (a real refetch), not a fake
  // hardware sync heartbeat.
  useEffect(() => {
    const id = setInterval(() => setClockNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => { setLastSync(Date.now()); }, [attData]);

  // Load the real saved attendance settings (geo-fence/face-verify toggles,
  // etc.) once on mount. Without this, `attSettings` stayed null forever and
  // the Settings tab silently fell back to the hardcoded defaults below —
  // both toggles shown as "Enabled" regardless of what was actually saved,
  // so saving any other field on that form (e.g. office start time) would
  // overwrite a real "disabled" back to "enabled" in the database.
  useEffect(() => {
    if (!can('attendance', 'settings', null, 'view')) return;
    api.get('/api/attendance/settings').then(d => setAttSettings(d)).catch(() => { });
  }, [role]);

  // Auto-load My Attendance, Team Report, and Regularization Log whenever
  // the tab is opened or month/year selector changes. Manual Load is gone.
  useEffect(() => {
    const { year, month } = attMonthYear;
    if (attTab === 'mine') {
      const cqs = company && company !== 'all' ? '&company=' + company : '';
      api.get(`/api/attendance/employee/0?year=${year}&month=${month}${cqs}`).then(d => setAttMyHistory(d)).catch(() => { });
    } else if (attTab === 'monthly') {
      const cqs = company && company !== 'all' ? '?company=' + company : '';
      api.get(`/api/attendance/monthly/${year}/${month}${cqs}`).then(d => setAttMonthly(Array.isArray(d) ? d : [])).catch(() => { });
    } else if (attTab === 'reg_log') {
      const params = new URLSearchParams({ year: String(year), month: String(month) });
      if (regLogFilter.status) params.set('status', regLogFilter.status);
      if (regLogFilter.employee_id) params.set('employee_id', regLogFilter.employee_id);
      api.get('/api/attendance/regularizations/history?' + params).then(d => setRegLog(Array.isArray(d) ? d : [])).catch(() => { });
    }
  }, [attTab, attMonthYear.year, attMonthYear.month, company, regLogFilter.status, regLogFilter.employee_id]);

  const doCheckIn = async () => {
    setAttCheckState(p => ({ ...p, loading: true, error: null }));
    try {
      const r = await api.post('/api/attendance/checkin', {
        latitude: attCheckState.coords?.lat,
        longitude: attCheckState.coords?.lng,
        accuracy: attCheckState.coords?.accuracy,
        face_verified: attCheckState.faceVerified,
        face_photo: attCheckState._facePhoto || null
      });
      setAttCheckState(p => ({ ...p, step: "done", result: r, loading: false }));
      onRefresh();
    } catch (e) {
      const msg = e.message || "Check-in failed";
      const alreadyCheckedIn = msg.toLowerCase().includes("already checked in");
      const locMismatch = msg.toLowerCase().includes("location mismatch");
      const policyAck = e.data?.code === "POLICY_ACK_REQUIRED";
      setAttCheckState(p => ({
        ...p,
        step: alreadyCheckedIn ? "already_in" : policyAck ? "policy_ack" : locMismatch ? "loc_error" : "face",
        error: msg, loading: false,
        pendingPolicies: policyAck ? (e.data.policies || []) : p.pendingPolicies,
      }));
    }
  };
  const doCheckOut = async () => {
    setAttCheckState(p => ({ ...p, loading: true, error: null }));
    try {
      const r = await api.post('/api/attendance/checkout', {
        latitude: attCheckState.coords?.lat,
        longitude: attCheckState.coords?.lng,
        accuracy: attCheckState.coords?.accuracy,
        face_verified: attCheckState.faceVerified,
        face_photo: attCheckState._facePhoto || null
      });
      setAttCheckState(p => ({ ...p, step: "checkedout", result: r, loading: false }));
      onRefresh();
    } catch (e) {
      const msg = e.message || "Check-out failed";
      const locMismatch = msg.toLowerCase().includes("location mismatch");
      setAttCheckState(p => ({ ...p, step: locMismatch ? "loc_error_out" : p.step, error: msg, loading: false }));
    }
  };
  const getGPS = (mode = "checkin") => {
    setAttCheckState(p => ({ ...p, step: "gps", error: null, _mode: mode, faceVerified: false, _facePhoto: null, _faceStatus: null, _camReady: false, _faceConfidence: null }));
    navigator.geolocation.getCurrentPosition(
      async pos => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
        setAttCheckState(p => ({ ...p, step: "loc_check", coords }));
        try {
          const path = mode === "checkout" ? '/api/attendance/checkout' : '/api/attendance/checkin';
          const preRes = await api.post(path, { latitude: coords.lat, longitude: coords.lng, accuracy: coords.accuracy, precheck: true });
          const geoMsg = preRes.geo_message || preRes.message || null;
          setAttCheckState(p => ({ ...p, step: "face", coords, geoMessage: geoMsg, error: null }));
        } catch (e) {
          const msg = e.message || "Location check failed";
          if (e.data?.code === "POLICY_ACK_REQUIRED") {
            setAttCheckState(p => ({ ...p, step: "policy_ack", coords, error: msg, pendingPolicies: e.data.policies || [] }));
            return;
          }
          setAttCheckState(p => ({ ...p, step: mode === "checkout" ? "loc_error_out" : "loc_error", coords, error: msg }));
        }
      },
      err => setAttCheckState(p => ({ ...p, step: "idle", error: "GPS denied: " + err.message })),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const TABS = [
    { k: "today", l: "Today's Overview", ic: CheckCircle },
    { k: "monthly", l: "Team Attendance Log", ic: BarChart2 },
    ...(can('attendance', 'reg_log', null, 'view') ? [{ k: "reg_log", l: "Regularization Requests", ic: PenLine, badge: attPendingRegs.length || null }] : []),
    { k: "holidays", l: "Holidays Calendar", ic: Calendar },
    // Bulk Regularization tab — visible to CH (to raise) and Admin (to
    // review the queue). HR Head deliberately excluded per policy.
    ...(can('attendance', 'bulk_reg', null, 'view') ? [{ k: "bulk_reg", l: "Bulk Regularization", ic: PenLine }] : []),
    { k: "mine", l: "My Attendance", ic: CalendarDays },
    ...(can('attendance', 'shift_requests', null, 'view') ? [{ k: "shift_requests", l: "Shift Requests", ic: Timer }] : []),
    { k: "settings", l: "Shift & Geo-fences", ic: Settings },
    ...(can("reports", "activity_log", null, "view") ? [{ k: "activity", l: "Activity Log", ic: History }] : []),
  ];
  const TabBar = () => (
    <div className="flex gap-[6px] mb-4 border-b border-b0 pb-[10px] flex-wrap whitespace-nowrap">
      {TABS.map(t => {
        if (t.k === "settings" && !can('attendance', 'settings', null, 'view')) return null;
        if (t.k === "monthly" && !can('attendance', 'monthly', null, 'view') && !hasReports) return null;
        return <button
          key={t.k}
          onClick={() => setAttTab(t.k)}
          className={`flex items-center gap-[6px] px-[14px] py-[6px] rounded-lg border-0 font-semibold text-[14px] cursor-pointer ${attTab === t.k ? "text-p" : "bg-transparent text-t3"}`}
          style={attTab === t.k ? { background: K.p + "22" } : undefined}
        >
          <t.ic size={12} />{t.l}
          {!!t.badge && <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[11px] font-extrabold text-white" style={{ background: K.r }}>{t.badge}</span>}
        </button>;
      })}
    </div>
  );

  // Shared hero banner — same brand gradient as Dashboard/My Profile, reused
  // across every Attendance tab (not just Today's Overview) so the whole
  // page reads as one consistent surface instead of one styled tab bolted
  // onto five plain ones.
  const Banner = ({ subtitle }) => (
    <div className="relative overflow-hidden rounded-2xl mb-4 px-6 py-5 sm:px-7 sm:py-6" style={{ background: "linear-gradient(90deg, #0a2364 0%, #0b4d79 55%, #059669 100%)" }}>
      <div className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay">
        <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1200 240" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="220" opacity="0.25" r="180" stroke="white" strokeWidth="48" />
          <circle cx="340" cy="240" opacity="0.2" r="220" stroke="white" strokeWidth="56" />
          <path d="M750 -40C860 60 980 120 1220 100" opacity="0.15" stroke="white" strokeWidth="64" />
        </svg>
      </div>
      <div className="relative">
        <h1 className="m-0 text-white text-2xl font-extrabold">Attendance &amp; Logs</h1>
        {subtitle && <div className="text-[14px] text-white/70 mt-[4px]">{subtitle}</div>}
      </div>
    </div>
  );

  const stC = (s) => s === "Present" ? K.g : s === "WFH" ? K.bl : s === "Late" ? K.a : s === "Half Day" ? K.pu : s === "Absent" ? K.r : s === "Leave" ? K.cy : s === "Leave Pending" ? K.a : s === "Holiday" ? K.tl : s === "Week Off" ? K.t3 : s === "Yet to Check In" ? K.a : K.t3;
  const fmtHM = (h) => { const n = parseFloat(h); if (isNaN(n) || n <= 0) return "—"; const m = Math.round(n * 60); const hh = Math.floor(m / 60), mm = m % 60; return hh === 0 ? `${mm}m` : mm === 0 ? `${hh}h` : `${hh}h ${mm}m`; };
  const photoLightbox = photoView && (() => {
    const r = photoView.record;
    const ts = (t) => t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: getAppTimezone() }) : "—";
    // location_type is only ever computed once, at check-in — there's no
    // separate categorized zone for checkout, just its raw coordinates
    // (checked at checkout time against a tolerance from the morning
    // location, not re-classified into Office/Home/Alt). So check-in shows
    // the zone badge; both show a "View on map" link when coordinates exist.
    const mapLink = (lat, lng) => (lat != null && lng != null) ? `https://www.google.com/maps?q=${lat},${lng}` : null;
    const photos = [
      r.check_in_photo && { label: "Check-In Selfie", src: uploadUrl(r.check_in_photo), time: ts(r.check_in), color: K.g, key: "in", locationType: r.location_type, mapUrl: mapLink(r.check_in_lat, r.check_in_lng) },
      r.check_out_photo && { label: "Check-Out Selfie", src: uploadUrl(r.check_out_photo), time: ts(r.check_out), color: K.bl, key: "out", mapUrl: mapLink(r.check_out_lat, r.check_out_lng) },
    ].filter(Boolean);
    return <div className="fixed inset-0 animate-fadeIn bg-black/85 z-[400] flex items-center justify-center p-6" onMouseDown={() => setPhotoView(null)}>
      <button onClick={() => setPhotoView(null)} className="fixed top-4 right-4 bg-white/10 border border-white/20 text-white cursor-pointer rounded-full w-9 h-9 flex items-center justify-center"><X size={18} /></button>
      <div className="animate-modalIn flex gap-[18px] max-w-full max-h-full items-center justify-center flex-wrap" onMouseDown={e => e.stopPropagation()}>
        {photos.map(p => <div key={p.key} className="text-center">
          <img src={p.src} alt={p.label} className="max-h-[70vh] rounded-[14px] border-4 shadow-[0_20px_60px_rgba(0,0,0,0.5)] block mx-auto" style={{ maxWidth: "min(420px,90vw)", borderColor: p.color }} />
          <div className="mt-[10px] text-white"><div className="text-[15px] font-bold" style={{ color: p.color }}>{p.label}</div><div className="text-[14px] opacity-85">{r.employee_name || ""} · {fmtDMY(r.date)} · {p.time}</div>
            <div className="mt-[6px] flex items-center justify-center gap-2">
              {p.locationType && <Bg c={p.locationType === "Office" ? K.g : p.locationType === "Home" ? K.bl : p.locationType === "Alt Location" ? K.pu : K.a} sm>{p.locationType}</Bg>}
              {p.mapUrl && <a href={p.mapUrl} target="_blank" rel="noreferrer" className="text-[14px] font-semibold inline-flex items-center gap-1" style={{ color: K.bl }}><MapPin size={11} />View on map</a>}
            </div>
          </div>
        </div>)}
        {photos.length === 0 && <div className="text-white text-[15px]">No selfie attached.</div>}
      </div>
    </div>;
  })();

  // Shared across every tab that can open a day's detail (Today's Records
  // table, Team Report calendar, My Attendance calendar) so clicking a day
  // always shows the popup on the tab you clicked from instead of only
  // appearing after switching to Today's View.
  const manualAttModal = manualAtt && canManualAtt && <ManualAttendanceModal
    key={(manualAtt.employeeId || "new") + "|" + (manualAtt.date || "")}
    initial={manualAtt}
    employees={todayRecs.map(r => ({ v: r.emp_code, l: `${r.employee_name} (${r.emp_code})` }))}
    canDelete={can('attendance', 'today', 'manual_attendance', 'delete')}
    onClose={() => setManualAtt(null)}
    onSaved={() => { onRefresh?.(); }}
  />;
  const attRecDetModal = attRecDet && <AttendanceDetailModal
    key={attRecDet.id ?? attRecDet.emp_code}
    rec={attRecDet}
    role={role}
    can={can}
    officeList={officeList}
    onClose={() => setAttRecDet(null)}
    onPhotoClick={(which) => setPhotoView({ record: attRecDet, which })}
    onSaveNotes={async (notesText) => {
      try {
        await api.put('/api/attendance/record/' + attRecDet.id + '/notes', { notes: notesText });
        setAttRecDet(p => ({ ...p, notes: notesText }));
        notify("Notes saved", { title: "Saved", variant: "success" });
      } catch (e) { notify(e.message || "Failed to save notes", { title: "Error", variant: "error" }); }
    }}
  />;

  if (attTab === "today") {
    const totalActive = sm.total || 0;
    const presentCount = (sm.present_office || 0) + (sm.present_wfh || 0);
    const presentPct = totalActive ? Math.round(presentCount / totalActive * 1000) / 10 : 0;
    const lateRate = totalActive ? Math.round((sm.late || 0) / totalActive * 1000) / 10 : 0;
    const plannedLeave = todayRecs.filter(r => r.status === 'Leave').length;
    const pendingLeave = todayRecs.filter(r => r.status === 'Leave Pending').length;
    const unexcusedAbsent = todayRecs.filter(r => r.status === 'Absent').length;
    const leavePool = plannedLeave + pendingLeave + unexcusedAbsent;
    const leavePoolPct = totalActive ? Math.round(leavePool / totalActive * 1000) / 10 : 0;
    // Grace window = office start time + the configured late threshold —
    // a real setting from Shift & Geo-fences, not a hardcoded number.
    const graceWindowStr = (() => {
      const [h, m] = String(sett.office_start_time || "09:30").split(":").map(Number);
      const d = new Date(); d.setHours(h || 0, (m || 0) + (parseInt(sett.late_threshold_minutes) || 0), 0, 0);
      return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    })();
    const syncSecAgo = Math.max(0, Math.round((clockNow - lastSync) / 1000));
    const syncLabel = syncSecAgo < 5 ? "just now" : syncSecAgo < 60 ? `${syncSecAgo}s ago` : `${Math.round(syncSecAgo / 60)}m ago`;

    const KPI_CARDS = [
      { icon: CheckCircle, label: "Present Today", value: presentCount, suffix: totalActive ? ` /${totalActive}` : "", c: K.g, sub: `${sm.present_office || 0} Office · ${sm.present_wfh || 0} WFH`, tag: totalActive ? `${presentPct}%` : null, tagColor: K.g },
      { icon: Timer, label: "Late Arrivals", value: sm.late || 0, c: K.a, sub: `Grace window: up to ${graceWindowStr}`, tag: totalActive ? `${lateRate}%` : null, tagColor: K.a },
      { icon: XCircle, label: "On Leave / Absent", value: leavePool, c: K.r, sub: `${plannedLeave} Planned Leave · ${pendingLeave} Approval Pending · ${unexcusedAbsent} Unexcused`, tag: totalActive ? `${leavePoolPct}%` : null, tagColor: K.r },
      { icon: PenLine, label: "Pending Approvals", value: attPendingRegs.length, c: K.pu, sub: "Attendance correction requests", tag: attPendingRegs.length > 0 ? "Review" : null, tagColor: K.pu },
    ];

    const STATUS_OPTIONS = ["Present", "Late", "Half Day", "WFH", "Yet to Check In", "Absent", "Leave", "Leave Pending", "Week Off", "Holiday"];
    const departments = Array.from(new Set(todayRecs.map(r => r.department).filter(Boolean))).sort();
    const searchQ = todaySearch.trim().toLowerCase();
    const filteredRecs = todayRecs.filter(r =>
      (!todayFilter.status || r.status === todayFilter.status) &&
      (!todayFilter.office || String(r.emp_office_id) === String(todayFilter.office)) &&
      (!todayFilter.dept || r.department === todayFilter.dept) &&
      (!searchQ || (r.employee_name || "").toLowerCase().includes(searchQ) || (r.emp_code || "").toLowerCase().includes(searchQ) || (r.department || "").toLowerCase().includes(searchQ))
    );
    const totalPages = Math.max(1, Math.ceil(filteredRecs.length / todayPageSize));
    const safePage = Math.min(todayPage, totalPages);
    const pageStart = (safePage - 1) * todayPageSize;
    const pagedRecs = filteredRecs.slice(pageStart, pageStart + todayPageSize);
    const pageNumbers = (() => {
      if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
      const set = new Set([1, totalPages, safePage, safePage - 1, safePage + 1]);
      const arr = Array.from(set).filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
      const withEllipsis = [];
      arr.forEach((n, i) => { if (i > 0 && n - arr[i - 1] > 1) withEllipsis.push('…'); withEllipsis.push(n); });
      return withEllipsis;
    })();
    const exportTodayCsv = () => {
      const header = ["Employee", "Employee ID", "Department", "Location", "First In", "Last Out", "Shift Hours", "Status"];
      const rows = filteredRecs.map(r => [
        r.employee_name || "", r.emp_code || "", r.department || "", r.office_name || "",
        r.check_in ? fmtTime(r.check_in) : "", r.check_out ? fmtTime(r.check_out) : "",
        fmtHM(r.work_hours), r.is_late ? `${r.status} (+${r.late_minutes}m)` : (r.status || ""),
      ]);
      const csv = [header, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `attendance_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    return <div>
    <div className="relative overflow-hidden rounded-2xl mb-4 px-6 py-5 sm:px-7 sm:py-6" style={{ background: "linear-gradient(90deg, #0a2364 0%, #0b4d79 55%, #059669 100%)" }}>
      <div className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay">
        <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1200 240" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="220" opacity="0.25" r="180" stroke="white" strokeWidth="48" />
          <circle cx="340" cy="240" opacity="0.2" r="220" stroke="white" strokeWidth="56" />
          <path d="M750 -40C860 60 980 120 1220 100" opacity="0.15" stroke="white" strokeWidth="64" />
        </svg>
      </div>
      <div className="relative flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-[10px] flex-wrap">
            <h1 className="m-0 text-white text-2xl font-extrabold">Attendance &amp; Logs</h1>
            <span className="inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wide text-white bg-white/15 rounded-full px-[10px] py-[3px]"><span className="w-[6px] h-[6px] rounded-full bg-g" />Live Monitor</span>
          </div>
          <div className="text-[14px] text-white/85 mt-[6px]">{new Date().toLocaleDateString("en-IN", { weekday: "long" })}, {fmtDMY(new Date())}</div>
          <div className="text-[14px] text-white/60 mt-[2px]">Real-time check-ins, geofenced mobile check-ins, and face-verified shift tracking.</div>
        </div>
        <div className="flex items-center gap-[10px] flex-wrap flex-shrink-0">
          <div className="text-[12px] text-white/60 font-semibold">Synced {syncLabel}</div>
          <button onClick={exportTodayCsv} className="flex items-center gap-[7px] bg-white/15 hover:bg-white/25 transition-colors text-white text-[14px] font-bold rounded-xl px-4 py-[10px] border border-white/20 cursor-pointer">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>
    </div>
    <TabBar />
    <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
      {KPI_CARDS.map((s, i) => (
        <div key={i} className="relative bg-bg1 border border-b0 rounded-2xl px-4 py-[14px] overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: s.c }} />
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-[10px] flex-shrink-0" style={{ background: s.c }}>
              <s.icon size={16} color="#fff" />
            </div>
            {s.tag && <Bg c={s.tagColor || K.p} sm>{s.tag}</Bg>}
          </div>
          <div className="text-[22px] font-extrabold leading-tight text-t0 mt-[8px]">{s.value}{s.suffix && <span className="text-[14px] text-t3 font-bold">{s.suffix}</span>}</div>
          <div className="text-[12px] text-t2 font-bold uppercase tracking-wide leading-tight mt-[1px]">{s.label}</div>
          {s.sub && <div className="text-[12px] text-t3 mt-[4px] truncate">{s.sub}</div>}
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-[14px]">
      <Cd>
        <Sec icon={ScanFace} title="Self Punch & Shift Timer" />
        {(() => {
          const myEmpId = myProfile?.basic?.employee_id || myProfile?.employee_id;
          const myTodayRec = myEmpId ? todayRecs.find(r => r.employee_id === myEmpId || r.emp_code === myEmpId) : null;
          // An overnight shift (e.g. 8pm → 6am) checks in on one calendar
          // day and checks out on the next, so myTodayRec (matched by
          // exact date) is empty while the shift is still open past
          // midnight. my_open_record finds the open row regardless of
          // date, and checkout_window_open only stays true within the
          // 5h-past-shift-end window — once that passes we treat it as
          // closed (show Check In again) even if the nightly sweep hasn't
          // flipped the row to Absent yet.
          const myOpenRecord = att.my_open_record || null;
          const activeRec = (myOpenRecord && myOpenRecord.checkout_window_open) ? myOpenRecord : myTodayRec;
          const isCheckedIn = !!(myOpenRecord && myOpenRecord.checkout_window_open);
          const isCheckedOut = myTodayRec && myTodayRec.check_out;
          // Profile-photo gate — mirrors the server-side block. Show a
          // proactive banner INSTEAD of the Check In button so the
          // employee sees WHY they can't mark attendance before they
          // click and get an error. Matches routes/attendance.js /checkin,
          // /checkout, /regularize which all return 400 NO_PROFILE_PHOTO
          // when profile_photo is null.
          const myProfilePhoto = myProfile?.basic?.profile_photo || myProfile?.profile_photo || null;
          const needsPhoto = !myProfilePhoto && !isCheckedOut;
          if (attCheckState.step === "idle") return <div>
            <div className="text-[14px] text-t2 mb-[10px]">Office Hours: <strong className="text-t0">{sett.office_start_time} – {sett.office_end_time}</strong> · Min: <strong className="text-t0">{sett.minimum_working_hours}h</strong></div>
            {needsPhoto ? <div className="bg-rb rounded-[10px] p-[14px] border border-rd">
              <div className="flex items-start gap-[8px]">
                <AlertTriangle size={18} color={K.r} className="flex-shrink-0 mt-[1px]" />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-bold text-r mb-[3px]">Profile photo required</div>
                  <div className="text-[14px] text-t1 leading-[1.5]">
                    You cannot check in or out until your profile photo is on record. Ask HR to upload it from Employees &gt; your profile &gt; photo. Regularization requests are blocked for the same reason.
                  </div>
                </div>
              </div>
            </div>
              : isCheckedOut ? <div className="bg-gb rounded-[10px] p-[14px] border border-gd text-center">
                <CheckCircle size={24} color={K.g} className="mx-auto mb-[6px]" />
                <div className="text-[14px] font-bold text-g">Done for today</div>
                <div className="text-[14px] text-t2 mt-1">Check-in: {fmtTime(myTodayRec.check_in)} · Check-out: {fmtTime(myTodayRec.check_out)} · {fmtHM(myTodayRec.work_hours)}</div>
              </div>
                : isCheckedIn ? <div>
                  <div className="rounded-2xl p-4 text-center mb-[10px]" style={{ background: "linear-gradient(160deg, rgba(16,185,129,.10), rgba(16,185,129,.03))", border: "1px solid rgba(16,185,129,.22)" }}>
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-full mb-[8px]" style={{ background: K.g }}>
                      <CheckCircle size={18} color="#fff" />
                    </div>
                    <div className="text-[14px] font-bold text-g">Checked in at {fmtTime(activeRec.check_in)}</div>
                    <div className="text-[12px] text-t3 mb-[10px]">{activeRec.location_type} · {activeRec.status}</div>
                    <LiveDuration since={activeRec.check_in} minHours={sett.minimum_working_hours} />
                  </div>
                  <Bt icon={LogOut} full onClick={() => getGPS("checkout")}>Check Out</Bt>
                </div>
                  : <div className="flex gap-2">
                    <Bt icon={Navigation} full onClick={getGPS}>Check In</Bt>
                  </div>}
          </div>;
          return null;
        })()}
        {attCheckState.step === "gps" && <div className="text-center p-4"><Navigation size={28} color={K.bl} className="mx-auto" /><div className="mt-2 text-[14px] text-t2">Getting your GPS location...</div><div className="text-[12px] text-t3 mt-1">Allow location access when browser asks</div></div>}
        {attCheckState.step === "loc_check" && <div className="text-center p-4"><MapPin size={28} color={K.bl} className="mx-auto" /><div className="mt-2 text-[14px] text-t2">Verifying location...</div>{attCheckState.coords && <div className="text-[12px] text-t3 mt-1">📍 {attCheckState.coords.lat.toFixed(5)}, {attCheckState.coords.lng.toFixed(5)}{attCheckState.coords.accuracy ? ` (±${Math.round(attCheckState.coords.accuracy)}m accuracy)` : ""}</div>}</div>}
        {attCheckState.step === "policy_ack" && <PolicyAckGateStep attCheckState={attCheckState} setAttCheckState={setAttCheckState} getGPS={getGPS} />}
        {(attCheckState.step === "loc_error" || attCheckState.step === "loc_error_out") && (() => {
          // This step actually fires for ANY error the /checkin|/checkout
          // precheck returns (missing profile photo, office hours closed,
          // etc.), not just a real geofence miss — labeling every one of
          // them "Location Mismatch" was misleading (e.g. a "no profile
          // photo on record" block read as a GPS problem, and "Retry GPS"
          // does nothing for that case).
          const isPhotoBlock = /profile photo/i.test(attCheckState.error || "");
          return <div>
            <div className="bg-rb rounded-[10px] p-[14px] border border-rd text-center mb-[10px]">
              <XCircle size={28} color={K.r} className="mx-auto mb-[6px]" />
              <div className="text-[14px] font-bold text-r mb-1">{isPhotoBlock ? "Profile Photo Required" : "Location Mismatch"}</div>
              <div className="text-[14px] text-t2 px-2">{attCheckState.error || "You are outside the allowed zone."}</div>
              {!isPhotoBlock && attCheckState.coords && <div className="text-[12px] text-t3 mt-[6px]">📍 {attCheckState.coords.lat.toFixed(5)}, {attCheckState.coords.lng.toFixed(5)}{attCheckState.coords.accuracy ? ` (±${Math.round(attCheckState.coords.accuracy)}m accuracy)` : ""}</div>}
            </div>
            <div className="flex gap-[6px]">
              {!isPhotoBlock && <Bt icon={RefreshCw} full onClick={() => getGPS(attCheckState.step === "loc_error_out" ? "checkout" : "checkin")}>Retry GPS</Bt>}
              <Bt v="ghost" full onClick={() => setAttCheckState({ step: "idle", faceVerified: false, coords: null, error: null, loading: false, result: null })}>{isPhotoBlock ? "Close" : "Cancel"}</Bt>
            </div>
          </div>;
        })()}
        {attCheckState.step === "face" && <FaceCheckStep attCheckState={attCheckState} setAttCheckState={setAttCheckState} myProfile={myProfile} doCheckIn={doCheckIn} doCheckOut={doCheckOut} />}
        {attCheckState.step === "already_in" && <div>
          <div className="bg-gb rounded-[10px] p-[14px] border border-gd text-center mb-[10px]">
            <CheckCircle size={28} color={K.g} className="mx-auto mb-[6px]" />
            <div className="text-[14px] font-bold text-g">Already Checked In Today</div>
            <div className="text-[14px] text-t2 mt-1">You can check out when you're done for the day.</div>
          </div>
          <Bt icon={LogOut} full onClick={() => getGPS("checkout")}>Check Out</Bt>
          <Bt v="ghost" full style={{ marginTop: 6 }} onClick={() => setAttCheckState({ step: "idle", faceVerified: false, coords: null, error: null, loading: false, result: null })}>Close</Bt>
        </div>}
        {attCheckState.step === "done" && <div>
          <div className="bg-gb rounded-[10px] p-[14px] border border-gd text-center mb-2">
            <CheckCircle size={28} color={K.g} className="mx-auto mb-[6px]" />
            <div className="text-[14px] font-bold text-g">Checked In Successfully!</div>
            <div className="text-[14px] text-t2 mt-1">{attCheckState.result?.location_type || "Office"} · {attCheckState.result?.is_late ? "Late" : "On Time"}</div>
            {attCheckState.result?.geo_message && <div className="text-[12px] text-t3 mt-1">{attCheckState.result.geo_message}</div>}
          </div>
          <Bt v="secondary" icon={LogOut} full onClick={() => getGPS("checkout")}>Check Out Later</Bt>
        </div>}
        {attCheckState.step === "checkedout" && <div className="bg-blb rounded-[10px] p-[14px] text-center" style={{ border: `1px solid ${K.bl}22` }}>
          <CheckCircle size={28} color={K.bl} className="mx-auto mb-[6px]" />
          <div className="text-[14px] font-bold text-bl">Checked Out!</div>
          <div className="text-[14px] text-t0 mt-1">Total: <strong>{fmtHM(attCheckState.result?.work_hours || 0)}</strong></div>
          {attCheckState.result?.warning && <div className="text-[12px] text-a mt-1">{attCheckState.result.warning}</div>}
        </div>}
        {attCheckState.error && <div className="mt-2 p-2 bg-rb rounded-lg border border-rd text-[14px] text-r">{attCheckState.error}</div>}
        {attCheckState.step === "idle" && (() => {
          const myEmpId1 = myProfile?.basic?.employee_id || myProfile?.employee_id;
          const myTodayRec1 = myEmpId1 ? todayRecs.find(r => r.employee_id === myEmpId1 || r.emp_code === myEmpId1) : null;
          const myOpenRecord1 = att.my_open_record || null;
          const activeRec1 = (myOpenRecord1 && myOpenRecord1.checkout_window_open) ? myOpenRecord1 : myTodayRec1;
          const isCheckedIn1 = !!(myOpenRecord1 && myOpenRecord1.checkout_window_open);
          const isCheckedOut1 = myTodayRec1 && myTodayRec1.check_out;
          const myProfilePhoto1 = myProfile?.basic?.profile_photo || myProfile?.profile_photo || null;
          const firstPunch = activeRec1?.check_in || myTodayRec1?.check_in || null;
          const estExit = firstPunch ? new Date(new Date(firstPunch).getTime() + (parseFloat(sett.minimum_working_hours) || 8) * 3600000).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: getAppTimezone() }) : null;
          const loggedNow = isCheckedIn1 ? fmtHM(Math.max(0, (clockNow - new Date(activeRec1.check_in).getTime()) / 3600000)) : isCheckedOut1 ? fmtHM(myTodayRec1.work_hours) : null;
          return <div>
            {loggedNow && <div className="grid grid-cols-3 gap-[8px] mt-3 pt-3 border-t border-b0">
              <div className="bg-pg rounded-xl px-[10px] py-[8px] min-w-0">
                <div className="flex items-center gap-[4px] text-[11px] text-t3 font-semibold uppercase tracking-wide"><Clock size={10} className="flex-shrink-0" /><span className="truncate">Logged Today</span></div>
                <div className="text-[14px] font-extrabold text-t0 mt-[3px] truncate">{loggedNow}<span className="text-t3 font-semibold text-[11px]"> /{sett.minimum_working_hours}h</span></div>
              </div>
              <div className="bg-pg rounded-xl px-[10px] py-[8px] min-w-0">
                <div className="flex items-center gap-[4px] text-[11px] text-t3 font-semibold uppercase tracking-wide"><Navigation size={10} className="flex-shrink-0" /><span className="truncate">First Punch</span></div>
                <div className="text-[14px] font-extrabold text-t0 mt-[3px] truncate">{firstPunch ? fmtTime(firstPunch) : "—"}</div>
              </div>
              <div className="bg-pg rounded-xl px-[10px] py-[8px] min-w-0">
                <div className="flex items-center gap-[4px] text-[11px] text-t3 font-semibold uppercase tracking-wide"><LogOut size={10} className="flex-shrink-0" /><span className="truncate">Est. Exit</span></div>
                <div className="text-[14px] font-extrabold text-t0 mt-[3px] truncate">{estExit || "—"}</div>
              </div>
            </div>}
            <div className="flex items-center justify-between flex-wrap gap-2 mt-3 pt-3 border-t border-b0 text-[12px]">
              <span className="inline-flex items-center gap-[6px] font-semibold px-[10px] py-[5px] rounded-full" style={{ background: myProfilePhoto1 ? K.gb : K.ab, color: myProfilePhoto1 ? K.g : K.a }}>
                {myProfilePhoto1 ? <CheckCircle size={12} /> : <AlertTriangle size={12} />}
                {myProfilePhoto1 ? "Face-ID Registered" : "Face-ID Not Registered"}
              </span>
              <button onClick={() => setAttTab('mine')} className="bg-transparent border-0 p-0 cursor-pointer font-semibold text-p hover:underline">View My Regularization History</button>
            </div>
          </div>;
        })()}
      </Cd>
      <Cd style={{ display: "flex", flexDirection: "column", maxHeight: 360 }}>
        <div className="flex justify-between items-center mb-2 flex-shrink-0"><Sec icon={Calendar} title="Upcoming Holidays" /></div>
        {(() => {
          // holData (GET /api/holidays) is already scoped server-side to
          // what this viewer is allowed to see — admin gets every office,
          // hr_head/centre_head get their Centre/Location Access offices
          // (or every office if unrestricted), everyone else gets just
          // their own office. No client-side re-filtering needed.
          const allHols = [];
          (holData || []).forEach(ofc => { (ofc.hols || []).forEach(h => { allHols.push({ ...h, office_name: ofc.nm || ofc.city, office_id: ofc.id }); }); });
          const today = new Date().toISOString().slice(0, 10);
          const upcoming = allHols.filter(h => h.date && h.date >= today).sort((a, b) => a.date.localeCompare(b.date));
          const months = {}; upcoming.forEach(h => { const m = h.date.slice(0, 7); if (!months[m]) months[m] = []; months[m].push(h); });
          // A holiday landing on a Monday or Friday stretches into a real
          // 3-day weekend — surfaced only when one actually exists in the
          // visible window, never guessed.
          const longWeekend = upcoming.find(h => { const dow = new Date(h.date + "T12:00:00").getDay(); return dow === 1 || dow === 5; });
          const daysToLongWeekend = longWeekend ? Math.round((new Date(longWeekend.date + "T12:00:00") - new Date(today + "T12:00:00")) / 86400000) : null;
          return upcoming.length > 0 ? <>
            <div className="overflow-y-auto flex-1 pr-1">
              {Object.entries(months).map(([m, hols]) => {
                const [y, mo] = m.split("-"); const monthName = new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
                return <div key={m} className="mb-3">
                  <div className="text-[12px] font-bold text-t3 uppercase tracking-[0.05em] mb-[6px]">{monthName}</div>
                  <div className="flex flex-col gap-[5px]">
                    {hols.map((h, i) => {
                      const d = new Date(h.date + "T12:00:00"); const dayName = d.toLocaleDateString("en-IN", { weekday: "short" }); const isNext = i === 0 && m === Object.keys(months)[0];
                      return <div key={h.id || i} className="flex items-center gap-[10px] px-3 py-2 rounded-[10px] border" style={{ background: isNext ? `${K.p}14` : K.pg, borderColor: isNext ? K.p + "30" : "transparent" }}>
                        <div className="w-10 h-10 rounded-[10px] flex flex-col items-center justify-center flex-shrink-0" style={{ background: isNext ? K.p : "#fff" }}>
                          <div className="text-[15px] font-extrabold leading-none" style={{ color: isNext ? "#fff" : K.t0 }}>{d.getDate()}</div>
                          <div className="text-[12px] font-semibold uppercase" style={{ color: isNext ? "#fff" : K.t3 }}>{dayName}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[14px] font-semibold text-t0 whitespace-nowrap overflow-hidden text-ellipsis">{h.name}</div>
                          <div className="text-[12px] text-t3 flex items-center gap-1"><MapPin size={8} />{h.office_name}</div>
                        </div>
                        <Bg c={h.type === "National" ? K.g : h.type === "Regional" ? K.bl : K.a} sm>{h.type || "Holiday"}</Bg>
                      </div>;
                    })}
                  </div>
                </div>;
              })}
            </div>
            {longWeekend && <div className="text-[12px] text-t3 font-semibold pt-2 mt-1 border-t border-b0 flex-shrink-0">Next long weekend starts in {daysToLongWeekend}d</div>}
          </> : <div className="text-center p-5 text-t3 text-[14px]">No upcoming holidays</div>;
        })()}
      </Cd>
    </div>
    {/* Today's Records — a team-attendance tool. Show it persistently to
        anyone with team visibility (admin / hr_head / centre_head, or any
        reporting manager via hasReports), including an empty state so it
        never silently disappears before the first check-in. Hide it
        entirely for plain employees: their scoped list is just their own
        row, which is redundant with the Check In/Out card above. The
        backend already scopes today_records to the viewer's hierarchy. */}
    {(can('attendance', 'monthly', null, 'view') || hasReports) && <Cd>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[16px] font-extrabold text-t0">Today's Records</div>
          <div className="text-[12px] text-t3 mt-[1px]">{todayRecs.length} Total · {todayRecs.filter(r => r.check_in).length} In</div>
        </div>
        <div className="flex items-center gap-[8px] flex-wrap">
          {canManualAtt && <Bt v="primary" s="sm" icon={PenLine} onClick={() => setManualAtt({})}>Mark Attendance</Bt>}
          <div className="w-[220px]"><Inp icon={Search} placeholder="Search employee, ID, department…" value={todaySearch} onChange={setTodaySearch} bg="bg-pg" /></div>
          <div className="w-[150px] flex-shrink-0"><Sl options={[{ v: "", l: "All Statuses" }, ...STATUS_OPTIONS.map(s => ({ v: s, l: s }))]} value={todayFilter.status} onChange={v => setTodayFilter(p => ({ ...p, status: v }))} bg="bg-pg" /></div>
          <div className="w-[160px] flex-shrink-0"><Sl options={[{ v: "", l: "All Locations" }, ...officeList.map(o => ({ v: o.id, l: o.name }))]} value={todayFilter.office} onChange={v => setTodayFilter(p => ({ ...p, office: v }))} bg="bg-pg" /></div>
          {departments.length > 0 && <div className="w-[160px] flex-shrink-0"><Sl options={[{ v: "", l: "All Departments" }, ...departments.map(d => ({ v: d, l: d }))]} value={todayFilter.dept} onChange={v => setTodayFilter(p => ({ ...p, dept: v }))} bg="bg-pg" /></div>}
        </div>
      </div>
      <div className="mb-3">
        <span className="inline-flex items-center px-3 py-[6px] rounded-lg text-[13px] font-bold" style={{ background: K.pg, color: K.p }}>
          Today, {fmtDMY(new Date())}
        </span>
      </div>
      {filteredRecs.length > 0 ? <>
        <Tbl cols={[
          { k: "e", l: "Employee", r: r => <div className="flex items-center gap-[10px] py-[3px]"><Av nm={r.employee_name} sz={36} /><div className="min-w-0"><div className="text-[14px] font-bold text-t0 truncate">{r.employee_name}</div><div className="text-[12px] text-t3 truncate">{r.emp_code} · {r.department}</div></div></div> },
          { k: "of", l: "Location", r: r => <div><div className="text-[13px] font-semibold text-t1">{r.office_name || "—"}</div></div> },
          { k: "io", l: "First In & Last Out", r: r => <div><div className="font-mono text-[13px] font-bold text-t0">{r.check_in ? fmtTime(r.check_in) : "—"}</div><div className="font-mono text-[12px] text-t3 mt-[1px]">{r.check_out ? fmtTime(r.check_out) : "—"}</div></div> },
          { k: "wh", l: "Shift Hours", r: r => {
              const target = parseFloat(sett.minimum_working_hours) || 8;
              const hrs = r.work_hours || 0;
              const c = hrs >= target ? K.g : hrs >= target / 2 ? K.a : K.t3;
              return <div className="min-w-[84px]">
                <span className="font-bold text-[14px]" style={{ color: c }}>{fmtHM(hrs)}</span>
                <span className="text-[12px] text-t3"> / {target}h req</span>
                <div className="mt-[4px] w-[74px]"><Pr value={hrs} max={target} c={c} h={4} grad="linear-gradient(90deg, #0a2364, #0b4d79, #059669)" /></div>
              </div>;
            } },
          { k: "st", l: "Status", r: r => <div><Bg c={stC(r.status)} dot sm>{r.is_late ? `${r.status} (+${fmtHM(r.late_minutes / 60)})` : (r.status === "Leave Pending" ? "Leave · Approval Pending" : r.status)}</Bg>{(r.status === "Leave" || r.status === "Leave Pending") && r.leave_type && <div className="text-[12px] text-t3 mt-[2px]">{r.leave_type}</div>}</div> },
          { k: "v", l: "Actions", r: r => <div className="flex gap-[4px] items-center"><Bt v="ghost" s="xs" icon={Eye} onClick={() => setAttRecDet(r)}>View</Bt>{canManualAtt && <Bt v="ghost" s="xs" icon={PenLine} onClick={() => setManualAtt({ employeeId: r.emp_code })}>Mark</Bt>}</div> }
        ]} data={pagedRecs} />
        <div className="flex items-center justify-between flex-wrap gap-3 mt-3 pt-3 border-t border-b0">
          <div className="flex items-center gap-[10px] text-[13px] text-t3 font-medium flex-wrap">
            <span>Showing {pageStart + 1}-{Math.min(pageStart + todayPageSize, filteredRecs.length)} of {filteredRecs.length} employees</span>
            <span className="flex items-center gap-[6px]">Rows per page
              <Sl w={68} options={[10, 25, 50, 100].map(n => ({ v: n, l: String(n) }))} value={todayPageSize} onChange={v => setTodayPageSize(parseInt(v))} />
            </span>
          </div>
          <div className="flex items-center gap-[3px]">
            <button disabled={safePage === 1} onClick={() => setTodayPage(p => Math.max(1, p - 1))} className={`w-7 h-7 rounded-lg flex items-center justify-center border-0 bg-transparent ${safePage === 1 ? "text-t3 opacity-40 cursor-not-allowed" : "text-t2 cursor-pointer hover:bg-bg2"}`}><ChevronLeft size={14} /></button>
            {pageNumbers.map((n, i) => n === '…'
              ? <span key={"e" + i} className="px-1 text-t3 text-[13px]">…</span>
              : <button key={n} onClick={() => setTodayPage(n)} className={`w-7 h-7 rounded-lg text-[13px] font-bold border-0 cursor-pointer ${n === safePage ? "text-white" : "bg-transparent text-t2 hover:bg-bg2"}`} style={n === safePage ? { background: K.p } : undefined}>{n}</button>
            )}
            <button disabled={safePage === totalPages} onClick={() => setTodayPage(p => Math.min(totalPages, p + 1))} className={`w-7 h-7 rounded-lg flex items-center justify-center border-0 bg-transparent ${safePage === totalPages ? "text-t3 opacity-40 cursor-not-allowed" : "text-t2 cursor-pointer hover:bg-bg2"}`}><ChevronRight size={14} /></button>
          </div>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 mt-4 pt-3 border-t border-b0 text-[12px] text-t3">
          <span>Face verification: <strong style={{ color: sett.face_verification_enabled ? K.g : K.r }}>{sett.face_verification_enabled ? "Enabled" : "Disabled"}</strong> · Geofencing: <strong style={{ color: sett.geo_fence_enabled ? K.g : K.r }}>{sett.geo_fence_enabled ? "Enabled" : "Disabled"}</strong></span>
          <span className="truncate max-w-[50%]">{officeList.map(o => o.name).join(" · ")}</span>
        </div>
      </> : <div className="text-center p-5 text-t3 text-[14px]"><ClipboardList size={20} color={K.t3} className="mx-auto mb-[6px]" style={{ opacity: 0.5 }} /><div>{todayRecs.length === 0 ? "No check-ins yet today" : "No records match these filters"}</div></div>}
    </Cd>}
    {photoLightbox}
    {attRecDetModal}{manualAttModal}
    {/* Show the approvals card to:
          - designated approver roles (admin / hr_head / centre_head), AND
          - ANY reporting manager (hasReports), regardless of system_role —
            most managers here carry the plain "employee" role but still
            have direct reports, so they must see this section even when
            their queue is empty, AND
          - anyone who happens to have pending requests (defensive).
        `hasReports` comes from /auth/me (COUNT of employees.manager_id = me).
        The backend scopes the list to the caller's own reports (or admin
        fallback), so surfacing the card is always safe.
        (Dropped the dead 'manager' role — no such system_role exists.) */}
    {(can('attendance', 'monthly', null, 'view') || hasReports || attPendingRegs.length > 0) && <div id="reg-approvals-card"><Cd style={{ marginTop: 12 }}>
      <div className="flex items-center justify-between mb-[10px] flex-wrap gap-2">
        <Sec icon={PenLine} title={`Regularization Approvals${attPendingRegs.length > 0 ? ' (' + attPendingRegs.length + ')' : ''}`} />
        <Bt v="ghost" s="sm" icon={RefreshCw} onClick={() => api.get('/api/attendance/regularizations').then(d => setAttPendingRegs(Array.isArray(d) ? d : [])).catch(console.error)}>Refresh</Bt>
      </div>
      {attPendingRegs.length === 0
        ? <div className="text-center p-5 text-[14px] text-t3"><CheckCircle size={20} color={K.g} className="mx-auto mb-[6px]" /><div>No pending regularization requests</div></div>
        : <Tbl cols={[
          { k: "e", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.employee_name} sz={22} /><div><div className="text-[15px] font-bold text-t0">{r.employee_name}{r.id === regFocusId && <span className="ml-[6px] text-[11px] font-bold px-[6px] py-[1px] rounded-full bg-ab text-a">from your notification</span>}</div><div className="text-[12px] text-t3">{r.emp_code} · {r.department}</div></div></div> },
          { k: "d", l: "Date", r: r => <span className="font-mono text-[14px]">{fmtDMY(r.date)}</span> },
          { k: "r", l: "Reason", r: r => <ReasonCell text={r.reason} maxW={160} /> },
          { k: "ci", l: "Req Check-In", r: r => <span className="text-[12px] font-mono">{r.requested_check_in ? fmtTime(r.requested_check_in) : "—"}</span> },
          { k: "co", l: "Req Check-Out", r: r => <span className="text-[12px] font-mono">{r.requested_check_out ? fmtTime(r.requested_check_out) : "—"}</span> },
          { k: "st", l: "Status", r: r => <div><Bg c={r.status === "Pending" ? K.a : r.status === "Approved" ? K.g : K.r} dot sm>{r.status}</Bg>{r.needs_senior && <div className="text-[12px] mt-[2px]" style={{ color: K.pu }}>#{r.month_seq} this month · needs CH / HR Head</div>}<RegSnapshot snap={r.pre_regularization_snapshot} /></div> },
          {
            k: "a", l: "Action", r: r => r.status === "Pending" ? <div className="flex gap-1">
              {(r.manager_id === myEmpIdAtt || can('attendance', 'reg_log', null, 'approve')) && <Bt v="success" s="xs" icon={Check} disabled={regActionBusy === `${r.id}:approve`} onClick={async () => { setRegActionBusy(`${r.id}:approve`); try { await api.put(`/api/attendance/regularize/${r.id}`, { status: "Approved" }); await api.get('/api/attendance/regularizations').then(d => setAttPendingRegs(Array.isArray(d) ? d : [])); notify("Regularization approved", { title: "Approved", variant: "success" }); } catch (e) { notify(e.message || "Failed to approve regularization", { title: "Error", variant: "error" }); } finally { setRegActionBusy(null); } }}>{regActionBusy === `${r.id}:approve` ? "Approving..." : "Approve"}</Bt>}
              {(r.manager_id === myEmpIdAtt || can('attendance', 'reg_log', null, 'reject')) && <Bt v="danger" s="xs" icon={X} disabled={regActionBusy === `${r.id}:reject`} onClick={async () => { const reason = await promptDialog("Reason for rejection:", { title: 'Reject regularization', multiline: true, variant: 'error', okLabel: 'Reject' }); if (reason === null) return; setRegActionBusy(`${r.id}:reject`); try { await api.put(`/api/attendance/regularize/${r.id}`, { status: "Rejected", remarks: reason || "Rejected" }); await api.get('/api/attendance/regularizations').then(d => setAttPendingRegs(Array.isArray(d) ? d : [])); notify("Regularization rejected", { title: "Rejected", variant: "success" }); } catch (e) { notify(e.message || "Failed to reject regularization", { title: "Error", variant: "error" }); } finally { setRegActionBusy(null); } }}>{regActionBusy === `${r.id}:reject` ? "Rejecting..." : "Reject"}</Bt>}
            </div> : <span className="text-[12px] text-t3">{r.approved_by || "—"}</span>
          }
        ]} data={attPendingRegs} />
      }
    </Cd></div>}
  </div>;
  }

  if (attTab === "holidays") {
    // holData (GET /api/holidays) is already scoped server-side to what
    // this viewer is allowed to see (see holidays.service.js:
    // resolveOfficeIds) — admin gets every office, hr_head/centre_head get
    // their Centre/Location Access offices (or every office if
    // unrestricted), everyone else gets just their own office. Admin
    // additionally gets a location filter to narrow the (otherwise
    // unrestricted) list down to one office — client-side, since admin
    // already has the full list in hand.
    const isAdmin = role === "admin";
    const allHols = [];
    (holData || []).forEach(ofc => { (ofc.hols || []).forEach(h => { allHols.push({ ...h, office_name: ofc.nm || ofc.city, office_id: ofc.id }); }); });
    const today = new Date().toISOString().slice(0, 10);
    const filtered = (isAdmin && holOfficeFilter !== "all") ? allHols.filter(h => String(h.office_id) === holOfficeFilter) : allHols;
    const upcoming = filtered.filter(h => h.date && h.date >= today).sort((a, b) => a.date.localeCompare(b.date));
    const months = {}; upcoming.forEach(h => { const m = h.date.slice(0, 7); if (!months[m]) months[m] = []; months[m].push(h); });
    return <div>
      <Banner subtitle="Holidays Calendar — offices, dates, and upcoming long weekends" />
      <TabBar />
      <Cd>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <Sec icon={Calendar} title={isAdmin ? "All Office Holidays" : "Your Office Holidays"} />
          {isAdmin && (holData || []).length > 0 && (
            <div className="w-[220px]">
              <Sl options={[{ v: "all", l: "All Offices" }, ...(holData || []).map(o => ({ v: String(o.id), l: o.nm || o.city || `Office ${o.id}` }))]} value={holOfficeFilter} onChange={setHolOfficeFilter} />
            </div>
          )}
        </div>
        {upcoming.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(months).map(([m, hols]) => {
            const [y, mo] = m.split("-"); const monthName = new Date(parseInt(y), parseInt(mo) - 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
            return <div key={m}>
              <div className="text-[12px] font-bold text-t3 uppercase tracking-[0.05em] mb-[6px]">{monthName}</div>
              <div className="flex flex-col gap-[5px]">
                {hols.map((h, i) => {
                  const d = new Date(h.date + "T12:00:00"); const dayName = d.toLocaleDateString("en-IN", { weekday: "short" });
                  return <div key={h.id || i} className="flex items-center gap-[10px] px-3 py-2 rounded-[10px]" style={{ background: K.pg }}>
                    <div className="w-10 h-10 rounded-[10px] flex flex-col items-center justify-center flex-shrink-0 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                      <div className="text-[15px] font-extrabold leading-none text-t0">{d.getDate()}</div>
                      <div className="text-[12px] font-semibold uppercase text-t3">{dayName}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] font-semibold text-t0 whitespace-nowrap overflow-hidden text-ellipsis">{h.name}</div>
                      <div className="text-[12px] text-t3 flex items-center gap-1"><MapPin size={8} />{h.office_name}</div>
                    </div>
                    <Bg c={h.type === "National" ? K.g : h.type === "Regional" ? K.bl : K.a} sm>{h.type || "Holiday"}</Bg>
                  </div>;
                })}
              </div>
            </div>;
          })}
        </div> : <div className="text-center p-10 text-t3 text-[14px]">No upcoming holidays</div>}
      </Cd>
    </div>;
  }

  if (attTab === "monthly") {
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return <div>
      <Banner subtitle="Team Attendance Log — monthly working days, WFH, late count, and averages per employee" />
      <TabBar />
      {/* Compact header — Sec heading on the left, fixed-width Month +
          Year dropdowns on the right (via the Sl `w` prop). */}
      <Cd style={{ marginBottom: 14, padding: 10 }}>
        <div className="flex items-center gap-[10px] flex-wrap justify-between">
          <Sec icon={BarChart2} title="Team Report" />
          <div className="flex items-center gap-[6px] flex-wrap">
            <div className="w-[200px]"><Inp placeholder="Search employee or ID…" value={attMonthlySearch} onChange={setAttMonthlySearch} /></div>
            <Sl w={110} options={MONTHS.map((m, i) => ({ v: i + 1, l: m }))} value={attMonthYear.month} onChange={v => setAttMonthYear(p => ({ ...p, month: parseInt(v) }))} />
            <Sl w={90} options={Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 5 + i).map(y => ({ v: y, l: String(y) }))} value={attMonthYear.year} onChange={v => setAttMonthYear(p => ({ ...p, year: parseInt(v) }))} />
          </div>
        </div>
      </Cd>
      {attMonthly.length > 0 && (() => {
        const q = attMonthlySearch.trim().toLowerCase();
        const filtered = q ? attMonthly.filter(r => r.name?.toLowerCase().includes(q) || r.emp_code?.toLowerCase().includes(q)) : attMonthly;
        return <>
          <KpiStrip items={[
            { icon: Users, label: q ? "Matching" : "Employees", value: filtered.length, c: K.p },
            { icon: CheckCircle, label: "Avg Working Days / Employee", value: filtered.length ? (filtered.reduce((s, r) => s + parseInt(r.working_days || 0), 0) / filtered.length).toFixed(1) : "0", c: K.g },
            { icon: Home, label: "Total WFH Days", value: filtered.reduce((s, r) => s + parseInt(r.wfh_days || 0), 0), c: K.bl },
            { icon: Timer, label: "Late Count", value: filtered.reduce((s, r) => s + parseInt(r.late_count || 0), 0), c: K.a },
            { icon: XCircle, label: "Total Absent", value: filtered.reduce((s, r) => s + parseInt(r.absent_days || 0), 0), c: K.r },
          ]} />
          <div className="text-[14px] text-t3 mb-2 flex items-center gap-1"><AlertTriangle size={11} /> Columns can overlap within the same day — e.g. one Late check-in at the office counts in both <strong>Office</strong> and <strong>Late</strong>. Hover a column header for details.</div>
          <Cd>
            {filtered.length > 0 ? <Tbl cols={[
              { k: "n", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.name} sz={22} src={r.profile_photo} /><div><div className="text-[15px] font-bold text-t0">{r.name}</div><div className="text-[12px] text-t3">{r.emp_code} · {r.department}</div></div></div> },
              { k: "wd", l: <span title="Days counted as Present, Late, WFH or Half Day. Days with status Absent are excluded.">Working</span>, r: r => <strong className="text-g">{r.working_days || 0}</strong> },
              { k: "od", l: <span title="Days where the check-in location was Office (on-site). Includes Late office check-ins.">Office</span>, r: r => <strong className="text-g">{r.office_days || 0}</strong> },
              { k: "wf", l: <span title="Days where location_type was Home or status was WFH.">WFH</span>, r: r => <strong className="text-bl">{r.wfh_days || 0}</strong> },
              { k: "ab", l: <span title="Days with final status Absent — check-in either never happened or worked less than half-day hours.">Absent</span>, r: r => <strong className="text-r">{r.absent_days || 0}</strong> },
              { k: "lc", l: <span title="Days with is_late=true. Cleared automatically when the day resolves to Absent or Half Day.">Late</span>, r: r => parseInt(r.late_count) > 0 ? <Bg c={K.a} sm>{r.late_count}</Bg> : <span className="text-t3">0</span> },
              { k: "ah", l: <span title="Average work_hours across days with a recorded check-out.">Avg Hrs</span>, r: r => <span className="font-bold" style={{ color: parseFloat(r.avg_hours || 0) >= 8 ? K.g : K.a }}>{r.avg_hours || "—"}</span> },
              { k: "ofc", l: "Office", r: r => <span className="text-[12px] text-t3">{r.office_name}</span> },
              { k: "v", l: "", r: r => <Bt v="ghost" s="xs" icon={Eye} disabled={teamViewLoadingId === r.emp_code} onClick={async () => { setTeamViewLoadingId(r.emp_code); try { const d = await api.get(`/api/attendance/employee/${r.emp_code}?year=${attMonthYear.year}&month=${attMonthYear.month}`); setTeamEmpAtt({ ...d, emp: r, year: attMonthYear.year, month: attMonthYear.month }); } catch (e) { notify(e.message || "Couldn't load attendance for this employee", { title: "Error", variant: "error" }); } finally { setTeamViewLoadingId(null); } }}>{teamViewLoadingId === r.emp_code ? "Loading..." : "View"}</Bt> }
            ]} data={filtered} /> : <div className="text-center p-10 text-t3 text-[14px]">No employee matches "{attMonthlySearch}"</div>}
          </Cd>
        </>;
      })()}
      {!attMonthly.length && <Cd><div className="text-center p-10 text-t3"><BarChart2 size={32} className="mx-auto mb-2 opacity-30" /><div>No attendance data for this month</div></div></Cd>}
      {teamEmpAtt && (() => {
        const ea = teamEmpAtt; const emp = ea.emp; const recs = ea.records || []; const yr = ea.year; const mo = ea.month;
        const MN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
        const dim = new Date(yr, mo, 0).getDate(); const today = new Date().toISOString().slice(0, 10);
        const days = []; for (let d = 1; d <= dim; d++) { const dt = `${yr}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`; const rec = recs.find(r => r.date && r.date.slice(0, 10) === dt); const dow = new Date(dt).getDay(); const isWeekend = ea.schedule?.[dt] ? ea.schedule[dt].t === "off" : (dow === 0 || dow === 6); days.push({ d, dt, rec, isWeekend, isFuture: dt > today, out: dayOutcome({ rec, calendar: ea.calendar?.[dt], isWeekend }) }); }
        // Absent / Leave counts come from the same per-day result the list shows.
        const absentDays = days.filter(x => !(x.isFuture && !x.rec)).reduce((n, x) => n + absentCount(x.out), 0);
        const leaveDays = days.reduce((n, x) => n + leaveCount(x.out), 0);
        const ft = (t) => t ? fmtTime(t) : "";
        const totalWorking = days.filter(x => x.rec && ["Present", "Late", "WFH", "Half Day"].includes(x.rec.status)).length;
        const totalHrs = recs.reduce((s, r) => s + (parseFloat(r.work_hours) || 0), 0);
        return <div className="fixed inset-0 animate-fadeIn bg-black/60 z-[200] flex items-stretch justify-center" onMouseDown={() => setTeamEmpAtt(null)}>
          <div className="animate-modalIn bg-bg w-full max-w-[1160px] flex flex-col shadow-[0_24px_80px_rgba(0,0,0,0.25)] overflow-hidden" onMouseDown={e => e.stopPropagation()}>
            <div className="px-6 py-5 text-white flex-shrink-0" style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
              <div className="flex justify-between items-start flex-wrap gap-3">
                <div className="flex items-center gap-[14px]">
                  <Av nm={emp.name} sz={48} rn="#fff" />
                  <div><div className="text-[18px] font-extrabold">{emp.name}</div><div className="text-[14px] opacity-80">{emp.emp_code} · {emp.department} · {emp.office_name}</div></div>
                </div>
                <button onClick={() => setTeamEmpAtt(null)} className="bg-white/20 border-0 text-white cursor-pointer rounded-lg p-[6px]"><X size={16} /></button>
              </div>
              <div className="flex gap-4 mt-4 flex-wrap">
                {[[totalWorking, "Days Present", K.g], [emp.wfh_days || 0, "WFH", K.bl], [emp.late_count || 0, "Late", K.a], [leaveDays, "Leave", K.pu], [absentDays, "Absent", K.r], [totalHrs ? `${(totalHrs / Math.max(totalWorking, 1)).toFixed(1)}h` : "—", "Avg/Day", K.p]].map(([v, l], i) =>
                  <div key={i} className="bg-white/15 rounded-[10px] px-[14px] py-2 flex-1 text-center min-w-[100px]">
                    <div className="text-[18px] font-extrabold">{v}</div>
                    <div className="text-[12px] opacity-70 mt-[2px]">{l}</div>
                  </div>
                )}
              </div>
              <div className="text-[14px] font-semibold mt-3 opacity-90">{MN[mo - 1]} {yr}</div>
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {days.map(({ d, dt, rec, isWeekend, isFuture, out }) => {
                if (isFuture && !rec) return null;
                // A real 'Absent' row (from the nightly auto-absent sweep) has
                // no check-in/check-out — render the same "Absent" callout as
                // a day with no record at all, not the normal present-day
                // layout (which just showed blank times/zone before). Still
                // clickable when a real row exists, Absent or not.
                const isPresent = rec && rec.status !== 'Absent';
                const wh = isPresent ? parseFloat(rec.work_hours) || 0 : 0;
                const whClr = wh >= 8 ? K.g : wh >= 4 ? K.a : wh > 0 ? K.r : K.t3;
                const dayLabel = new Date(dt + "T12:00:00").toLocaleDateString("en-IN", { weekday: "short" });
                if (out.kind === "weekend") return <div key={dt} className="flex items-center px-6 py-[6px] opacity-35">
                  <div className="w-11 flex-shrink-0"><div className="text-[12px] font-semibold text-t3">{dayLabel}</div><div className="text-[15px] font-bold text-t3">{d}</div></div>
                  <div className="flex-1 h-[1px] bg-b1 mx-3" /><div className="text-[12px] text-t3">Weekend</div>
                </div>;
                // Holiday or leave: say what it is instead of calling the day Absent.
                if (out.kind === "half_leave_absent") return <div key={dt} className="flex items-center px-6 py-[10px]" style={{ background: K.rb + "40", borderBottom: `1px solid ${K.b0}30` }}>
                  <div className="w-11 flex-shrink-0"><div className="text-[12px] font-semibold text-t3 uppercase">{dayLabel}</div><div className="text-[16px] font-extrabold" style={{ color: K.r }}>{d}</div></div>
                  <div className="flex-1 flex items-center gap-2 pl-2 flex-wrap"><Bg c={out.pending ? K.a : K.bl} dot>{halfLeaveText(out)}</Bg><Bg c={K.r} dot>{absentOtherHalfText(out)}</Bg></div>
                </div>;
                if (out.kind === "holiday" || out.kind === "leave" || out.kind === "leave_pending") {
                  const clr = out.kind === "holiday" ? K.pu : out.kind === "leave" ? K.bl : K.a;
                  return <div key={dt} className="flex items-center px-6 py-[10px]" style={{ background: clr + "12", borderBottom: `1px solid ${K.b0}30` }}>
                    <div className="w-11 flex-shrink-0"><div className="text-[12px] font-semibold text-t3 uppercase">{dayLabel}</div><div className="text-[16px] font-extrabold" style={{ color: clr }}>{d}</div></div>
                    <div className="flex-1 flex items-center gap-2 pl-2"><Bg c={clr} dot>{dayOutcomeText(out)}</Bg></div>
                  </div>;
                }
                return <div key={dt} onClick={() => { if (rec) setAttRecDet(rec); }} className={`flex items-center px-6 py-[10px] ${rec ? "cursor-pointer hover:bg-bg2" : "cursor-default"}`} style={{ background: isPresent ? "transparent" : K.rb + "40", borderBottom: `1px solid ${K.b0}30` }}>
                  <div className="w-11 flex-shrink-0"><div className="text-[12px] font-semibold text-t3 uppercase">{dayLabel}</div><div className="text-[16px] font-extrabold" style={{ color: isPresent ? K.t0 : K.r }}>{d}</div></div>
                  {isPresent ? <>
                    <div className="w-[72px] flex-shrink-0 text-center"><div className="text-[14px] font-bold text-g">{ft(rec.check_in)}</div>{rec.is_late && <div className="text-[12px] text-a font-semibold">Late {rec.late_minutes}m</div>}</div>
                    <div className="flex-1 px-3">
                      <div className="relative h-2 bg-bg3 rounded overflow-hidden"><div className="absolute left-0 top-0 h-full rounded" style={{ width: `${Math.min(wh / 10 * 100, 100)}%`, background: `linear-gradient(90deg,${whClr},${whClr}BB)` }} /></div>
                      <div className="flex justify-between mt-[3px]">
                        <span className="text-[12px] text-t3">9AM</span>
                        <Bg c={rec.location_type === "Office" ? K.g : rec.location_type === "Home" ? K.bl : rec.location_type === "Alt Location" ? K.pu : K.a} sm style={{ fontSize: 6, padding: "1px 5px" }}>{rec.location_type}</Bg>
                        <span className="text-[12px] text-t3">6PM</span>
                      </div>
                      {(out.halfLeave || rec.status === "Half Day") && <div className="flex justify-center gap-1 mt-[3px] flex-wrap">
                        {out.halfLeave && <Bg c={out.halfLeave.pending ? K.a : K.bl} sm>{halfLeaveText(out.halfLeave)}</Bg>}
                        {rec.status === "Half Day" && <Bg c={K.a} sm>Half Day</Bg>}
                      </div>}
                    </div>
                    <div className="w-[72px] flex-shrink-0 text-center"><div className="text-[14px] font-bold" style={{ color: rec.check_out ? K.bl : K.t3 }}>{ft(rec.check_out) || "—"}</div></div>
                    <div className="w-16 flex-shrink-0 text-center"><div className="text-[15px] font-extrabold" style={{ color: whClr }}>{fmtHM(wh)}</div></div>
                    <div className="w-8 flex-shrink-0 text-center">
                      {rec.check_in_photo ? <img src={uploadUrl(rec.check_in_photo)} alt="check-in selfie" title="Click to enlarge" onClick={(e) => { e.stopPropagation(); setPhotoView({ record: rec, which: "in" }); }} className="w-7 h-7 rounded-full object-cover border-2 border-g cursor-pointer" /> : <div className="w-7 h-7 rounded-full bg-bg3 flex items-center justify-center"><ScanFace size={12} color={K.t3} /></div>}
                    </div>
                  </> : <>
                    <div className="flex-1 flex items-center gap-2 pl-2">
                      <XCircle size={14} color={K.r} /><span className="text-[14px] font-semibold text-r">Absent</span>
                    </div>
                  </>}
                </div>;
              })}
            </div>
          </div>
        </div>;
      })()}
      {photoLightbox}
      {attRecDetModal}{manualAttModal}
    </div>;
  }

  if (attTab === "reg_log" && can('attendance', 'reg_log', null, 'view')) {
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 5 + i);
    const counts = regLog.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
    return <div>
      <Banner subtitle="Regularization Requests — audit view across employees" />
      <TabBar />
      <Cd style={{ marginBottom: 12 }}>
        <div className="flex items-center gap-2 flex-nowrap overflow-x-auto">
          <Sec icon={PenLine} title={`Regularization Log (${regLog.length})`} />
          <div className="w-[120px] flex-shrink-0">
            <Sl options={MONTHS.map((m, i) => ({ v: i + 1, l: m }))} value={attMonthYear.month} onChange={v => setAttMonthYear(p => ({ ...p, month: parseInt(v) }))} />
          </div>
          <div className="w-[100px] flex-shrink-0">
            <Sl options={YEARS.map(y => ({ v: y, l: String(y) }))} value={attMonthYear.year} onChange={v => setAttMonthYear(p => ({ ...p, year: parseInt(v) }))} />
          </div>
          <div className="w-[140px] flex-shrink-0">
            <Sl options={[{ v: "", l: "All statuses" }, { v: "Pending", l: "Pending" }, { v: "Approved", l: "Approved" }, { v: "Rejected", l: "Rejected" }]} value={regLogFilter.status} onChange={v => setRegLogFilter(p => ({ ...p, status: v }))} />
          </div>
          <div className="flex-1 min-w-[180px]">
            <Inp placeholder="Employee ID (e.g. E0051)" value={regLogFilter.employee_id} onChange={v => setRegLogFilter(p => ({ ...p, employee_id: v.trim().toUpperCase() }))} />
          </div>
        </div>
      </Cd>
      <KpiStrip items={[
        { icon: PenLine, label: "Total", value: regLog.length, c: K.p },
        { icon: Clock, label: "Pending", value: counts.Pending || 0, c: K.a },
        { icon: CheckCircle, label: "Approved", value: counts.Approved || 0, c: K.g },
        { icon: XCircle, label: "Rejected", value: counts.Rejected || 0, c: K.r },
      ]} />
      <Cd>
        {regLog.length === 0 ? (
          <div className="text-center p-10 text-t3"><PenLine size={24} className="mx-auto mb-2 opacity-30" /><div>No regularization requests match these filters</div></div>
        ) : (
          <Tbl cols={[
            { k: "e", l: "Employee", r: r => <div className="flex items-center gap-[5px]"><Av nm={r.employee_name} sz={22} /><div><div className="text-[15px] font-bold text-t0">{r.employee_name}</div><div className="text-[12px] text-t3">{r.emp_code} · {r.department || "—"}</div></div></div> },
            { k: "d", l: "Date", r: r => <span className="font-mono text-[14px]">{fmtDMY(r.date)}</span> },
            { k: "r", l: "Reason", r: r => <ReasonCell text={r.reason} maxW={180} /> },
            { k: "ci", l: "Req Check-In", r: r => <span className="text-[12px] font-mono">{r.requested_check_in ? fmtTime(r.requested_check_in) : "—"}</span> },
            { k: "co", l: "Req Check-Out", r: r => <span className="text-[12px] font-mono">{r.requested_check_out ? fmtTime(r.requested_check_out) : "—"}</span> },
            { k: "st", l: "Status", r: r => <div><Bg c={r.status === "Pending" ? K.a : r.status === "Approved" ? K.g : K.r} dot sm>{r.status}</Bg><RegSnapshot snap={r.pre_regularization_snapshot} /></div> },
            { k: "mgr", l: "Reporting Manager", r: r => r.manager_name ? <span className="text-[12px] text-t2">{r.manager_name}</span> : <span className="text-t3 text-[12px]">— (no manager)</span> },
            { k: "apb", l: "Decided by", r: r => r.approver_name ? <div className="flex items-center gap-[5px]"><Av nm={r.approver_name} sz={20} /><span className="text-[12px] text-t1 font-semibold">{r.approver_name}</span></div> : <span className="text-[12px] text-t3">—</span> },
            // Older requests saved "Approved by <login role>" (e.g. "Approved by employee") as the note — that is not a
            // remark, and the "Decided by" column now shows the actual person, so hide it.
            { k: "rm", l: "Remarks", r: r => { const t = (r.approval_remarks || "").trim(); const roleOnly = /^approved by (admin|hr_head|centre_head|centre_hr|manager|employee|recruiter|accounts)$/i.test(t); return <span className="text-[12px] text-t2">{t && !roleOnly ? t : "—"}</span>; } },
            { k: "sub", l: "Submitted", r: r => <span className="text-[12px] text-t3">{r.created_at ? fmtDMY(r.created_at) : "—"}</span> },
          ]} data={regLog} />
        )}
      </Cd>
    </div>;
  }

  if (attTab === "shift_requests" && can('attendance', 'shift_requests', null, 'view')) {
    return <div>
      <Banner subtitle="Shift Requests — ask to work a different shift for a range of dates, and approve your team's requests" />
      <TabBar />
      <ShiftRequestsTab openId={shiftReqOpenId} onOpenedId={() => { setShiftReqOpenId(null); clearHashQuery?.(); }} />
    </div>;
  }

  if (attTab === "activity" && can("reports", "activity_log", null, "view")) {
    return <div>
      <Banner subtitle="Activity Log — check-ins, regularizations and settings changes, and who made them" />
      <TabBar />
      <ActivityLog module="attendance" title="Attendance Activity Log" canDownload={can("reports", "activity_log", "download", "view")} />
    </div>;
  }

  if (attTab === "bulk_reg" && can('attendance', 'bulk_reg', null, 'view')) {
    return <div>
      <Banner subtitle="Bulk Regularization — raise or review multi-day attendance corrections" />
      <TabBar />
      <BulkRegularizationTab role={role} can={can} />
    </div>;
  }

  if (attTab === "mine") {
    const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    // Year dropdown: current year - 5 up to current year (no future years — nobody
    // backfills attendance for days that haven't happened yet).
    const thisYear = new Date().getFullYear();
    const YEARS = Array.from({ length: 6 }, (_, i) => thisYear - 5 + i);

    // Respect the employee's 6-day-week setting. Saturday is only "Off" when
    // saturday_working is false. Sunday is always weekend.
    const saturdayWorks = !!myProfile?.saturday_working;

    // Holidays for this user's office keyed by YYYY-MM-DD for O(1) lookup per cell.
    const myOfficeIdForCal = myProfile?.employment?.office_id || myProfile?.basic?.office_id || null;
    const holidayMap = {};
    (holData || []).forEach(ofc => {
      if (myOfficeIdForCal && ofc.id !== myOfficeIdForCal) return; // only show holidays that apply to me
      (ofc.hols || []).forEach(h => { if (h.date) holidayMap[h.date.slice(0, 10)] = h.name || "Holiday"; });
    });

    const calDays = [];
    const { year, month } = attMonthYear;
    const daysInMonth = new Date(year, month, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const rec = myRecs.find(r => r.date && r.date.slice(0, 10) === dt);
      const dow = new Date(dt).getDay();
      const isSunday = dow === 0;
      const isSaturday = dow === 6;
      // The weekly schedule (person → centre → default) decides week-offs;
      // fall back to the old Sunday / Saturday rule if the server sent none.
      const sch = attMyHistory?.schedule?.[dt];
      const isWeekend = sch ? sch.t === "off" : (isSunday || (isSaturday && !saturdayWorks));
      const dayKind = sch && (sch.t === "half_day" || sch.t === "wfh") ? sch.t : null;
      const holidayName = holidayMap[dt] || null;
      calDays.push({ d, dt, rec, isWeekend, holidayName, dayKind, sch });
    }
    return <div>
      <Banner subtitle="My Attendance History — your own check-ins, hours, and regularizations" />
      <TabBar />
      <div className="flex items-center gap-[6px] mb-[14px] flex-wrap">
        <Sl w={110} options={MONTHS.map((m, i) => ({ v: i + 1, l: m }))} value={month} onChange={v => setAttMonthYear(p => ({ ...p, month: parseInt(v) }))} />
        <Sl w={90} options={YEARS.map(y => ({ v: y, l: String(y) }))} value={year} onChange={v => setAttMonthYear(p => ({ ...p, year: parseInt(v) }))} />
      </div>
      <KpiStrip items={[
        { icon: CheckCircle, label: "Days Present", value: String(mySum.present || 0), c: K.g },
        { icon: Timer, label: "Late Days", value: String(mySum.late || 0), c: K.a },
        { icon: Home, label: "WFH Days", value: String(mySum.wfh || 0), c: K.bl },
        { icon: XCircle, label: "Absent", value: String(mySum.absent || 0), c: K.r },
        { icon: CheckCircle, label: "Avg Hours", value: String(mySum.avg_hours || "—"), c: K.p },
      ]} />
      <Cd style={{ marginBottom: 14 }}>
        <Sec icon={CalendarDays} title={`${MONTHS[month - 1]} ${year} Calendar`} />
        <div className="grid grid-cols-7 gap-1 mb-[6px]">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => <div key={d} className="text-center text-[12px] text-t3 font-semibold py-[2px]">{d}</div>)}
        </div>
        {(() => {
          const firstDay = new Date(year, month - 1, 1).getDay();
          const offset = (firstDay + 6) % 7;
          const allCells = [...Array(offset).fill(null), ...calDays];
          const rows = [];
          for (let i = 0; i < allCells.length; i += 7) rows.push(allCells.slice(i, i + 7));
          return rows.map((row, ri) => <div key={ri} className="grid grid-cols-7 gap-1 mb-1">
            {row.map((cell, ci) => {
              if (!cell) return <div key={ci} />;
              const { d, rec, isWeekend, holidayName, dayKind, sch } = cell;
              const isHoliday = !!holidayName && !rec;
              const bg = isHoliday ? `${K.pu}12` : isWeekend ? K.bg3 : rec ? `${stC(rec.status)}18` : K.bg2;
              const border = isHoliday ? `1px solid ${K.pu}44` : rec ? `1px solid ${stC(rec.status)}44` : `1px solid ${K.b0}`;
              const wh = rec ? parseFloat(rec.work_hours) || 0 : 0;
              const whColor = wh >= 8 ? K.g : wh >= 4 ? K.a : wh > 0 ? K.r : K.t3;
              const title = isHoliday ? holidayName : dayKind ? `${dayKind === "wfh" ? "Work from home day" : "Half day"}${sch?.s ? ` · ${sch.s}–${sch.e}` : ""}` : "";
              return <div key={ci} title={title} onClick={() => { if (rec) setAttRecDet(rec); }} className={`rounded-md px-1 py-[6px] text-center min-h-[48px] ${rec ? "cursor-pointer" : "cursor-default"}`} style={{ background: bg, border }}>
                <div className="text-[14px] font-bold mb-[2px]" style={{ color: isHoliday ? K.pu : isWeekend ? K.t3 : K.t1 }}>{d}</div>
                {rec && <Bg c={stC(rec.status)} sm style={{ fontSize: 7, padding: "1px 4px" }}>{rec.status === "Present" ? "P" : rec.status === "Late" ? "L" : rec.status === "WFH" ? "W" : rec.status === "Absent" ? "A" : rec.status === "Half Day" ? "H" : "?"}</Bg>}
                {rec && wh > 0 && <div className="text-[12px] font-bold mt-[2px]" style={{ color: whColor }}>{fmtHM(wh)}</div>}
                {isHoliday && <div className="text-[12px] font-semibold truncate" style={{ color: K.pu }}>{holidayName.length > 8 ? holidayName.slice(0, 8) + "…" : holidayName}</div>}
                {isWeekend && !rec && !isHoliday && <div className="text-[12px] text-t3">Off</div>}
                {dayKind && !rec && !isHoliday && <div className="text-[11px] font-bold" style={{ color: dayKind === "wfh" ? K.bl : K.pu }}>{dayKind === "wfh" ? "WFH day" : "Half day"}</div>}
              </div>;
            })}
          </div>);
        })()}
        <div className="flex gap-[10px] mt-[6px] flex-wrap">
          {[["Present", K.g], ["WFH", K.bl], ["Late", K.a], ["Absent", K.r], ["Half Day", K.a], ["Holiday", K.pu], ["Weekend", K.t3]].map(([l, c]) =>
            <div key={l} className="flex items-center gap-1 text-[12px] text-t2"><div className="w-2 h-2 rounded-[2px]" style={{ background: c }} />{l}</div>
          )}
        </div>
        {(() => {
          // Hours done this month (everyone) — and, for a flexible shift, how that compares with what the month needs.
          const recs = attMyHistory?.records || [];
          const total = recs.reduce((t, r) => t + (parseFloat(r.work_hours) || 0), 0);
          const daysWorked = recs.filter(r => (parseFloat(r.work_hours) || 0) > 0).length;
          const fx = attMyHistory?.flex;
          const pct = fx && fx.targetHours > 0 ? Math.min(100, (fx.workedHours / fx.targetHours) * 100) : 0;
          return <div className="mt-3 pt-3 border-t border-b0">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-[6px]">
              <div className="text-[14px] font-bold text-t0">{fx ? "Flexible hours" : "Hours worked"} — {MONTHS[month - 1]} {year}</div>
              {fx && <div className="text-[12px] text-t3">{fx.workingDays} working days × {fx.hoursPerDay} hrs{fx.leaveHoursExcluded > 0 ? ` − ${fx.leaveHoursExcluded} hrs leave` : ''} · a day under {fx.dailyMinHours} hrs = Half Day</div>}
            </div>
            <div className={`grid grid-cols-2 ${fx ? "sm:grid-cols-5" : "sm:grid-cols-3"} gap-2 mb-2`}>
              <div><div className="text-[12px] text-t3">Total hours done this month</div><div className="text-[17px] font-extrabold" style={{ color: K.g }}>{fmtHM(total)}</div></div>
              <div><div className="text-[12px] text-t3">Days with hours</div><div className="text-[17px] font-extrabold text-t0">{daysWorked}</div></div>
              {!fx && <div><div className="text-[12px] text-t3">Average per day</div><div className="text-[17px] font-extrabold text-t0">{daysWorked ? fmtHM(total / daysWorked) : "—"}</div></div>}
              {fx && <div><div className="text-[12px] text-t3">To complete this month</div><div className="text-[17px] font-extrabold text-t0">{fmtHM(fx.targetHours)}</div></div>}
              {fx && <div><div className="text-[12px] text-t3">Remaining</div><div className="text-[17px] font-extrabold" style={{ color: fx.remainingHours > 0 ? K.a : K.g }}>{fmtHM(fx.remainingHours)}</div></div>}
              {fx && <div><div className="text-[12px] text-t3">Target up to today</div><div className="text-[17px] font-extrabold text-t0">{fmtHM(fx.targetSoFar)}</div></div>}
            </div>
            {fx && <div className="h-[6px] rounded-full bg-bg2 overflow-hidden"><div className="h-full rounded-full" style={{ width: pct + '%', background: fx.workedHours >= fx.targetSoFar ? K.g : K.a }} /></div>}
          </div>;
        })()}
      </Cd>
      <Cd style={{ marginBottom: 14 }}>
        <Sec icon={PenLine} title="Request Regularization" />
        <div className="text-[14px] text-t3 mb-[10px]">You can regularize today and up to 3 days back. Your reporting manager approves the first 2 requests of a month; from the 3rd request onwards it needs your Centre Head / HR Head.</div>
        {!attRegForm && <Bt icon={CheckCircle} onClick={() => setAttRegForm({ date: "", reason: "", ci: "", co: "" })}>New Request</Bt>}
        {attRegForm && (() => {
          const today = new Date().toISOString().slice(0, 10);
          const minDate = (() => { const d = new Date(); d.setDate(d.getDate() - 3); return d.toISOString().slice(0, 10); })();
          const timeInvalid = attRegForm.ci && attRegForm.co && attRegForm.ci >= attRegForm.co;
          const canSubmit = attRegForm.date && attRegForm.date >= minDate && attRegForm.date <= today && attRegForm.reason.trim() && attRegForm.ci && attRegForm.co && !timeInvalid;
          return <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
              <div><div className="text-[12px] text-t2 mb-[3px]">Date <span className="text-r">*</span></div><input type="date" min={minDate} max={today} value={attRegForm.date} onChange={e => setAttRegForm(p => ({ ...p, date: e.target.value }))} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
              <div><div className="text-[12px] text-t2 mb-[3px]">Reason <span className="text-r">*</span></div><Inp placeholder="e.g. Forgot to check in" value={attRegForm.reason} onChange={v => setAttRegForm(p => ({ ...p, reason: v }))} /></div>
              <div><div className="text-[12px] text-t2 mb-[3px]">Check-In Time <span className="text-r">*</span></div><Inp type="time" value={attRegForm.ci} onChange={v => setAttRegForm(p => ({ ...p, ci: v }))} /></div>
              <div><div className="text-[12px] text-t2 mb-[3px]">Check-Out Time <span className="text-r">*</span></div><Inp type="time" value={attRegForm.co} onChange={v => setAttRegForm(p => ({ ...p, co: v }))} /></div>
            </div>
            {timeInvalid && <div className="text-[14px] text-r mb-2 flex items-center gap-1"><AlertTriangle size={11} />Check-out time must be after check-in time</div>}
            <div><div className="text-[12px] text-t2 mb-[3px]">Additional Notes</div><Inp placeholder="Any extra details..." value={attRegNote} onChange={setAttRegNote} /></div>
            {(() => {
              const mth = (attRegForm.date || '').slice(0, 7);
              const used = mth ? (attMyHistory?.regularizations || []).filter(r => r.status !== 'Rejected' && String(r.date).slice(0, 7) === mth).length : 0;
              return used >= 2 ? <div className="text-[14px] mt-2 px-[10px] py-[6px] rounded-lg flex items-center gap-1" style={{ background: K.a + '14', color: K.a }}><AlertTriangle size={12} />This will be your request #{used + 1} for that month — it will go to your Centre Head / HR Head for approval, not your manager.</div> : null;
            })()}
            <div className="flex gap-[6px] mt-2">
              <Bt icon={Send} disabled={!canSubmit || regSubmitting} onClick={async () => {
                setRegSubmitting(true);
                try {
                  const regRes = await api.post('/api/attendance/regularize', { date: attRegForm.date, reason: attRegForm.reason + (attRegNote ? " — " + attRegNote : ""), requested_check_in: `${attRegForm.date}T${attRegForm.ci}:00`, requested_check_out: `${attRegForm.date}T${attRegForm.co}:00` });
                  setAttRegForm(null); setAttRegNote("");
                  api.get(`/api/attendance/employee/0?year=${attMonthYear.year}&month=${attMonthYear.month}`).then(d => setAttMyHistory(d)).catch(console.error);
                  notify(regRes?.escalated ? `Request #${regRes.month_seq} this month submitted — it needs Centre Head / HR Head approval` : "Regularization request submitted", { title: "Submitted", variant: "success" });
                } catch (e) { notify(e.message || "Error submitting regularization request", { title: "Error", variant: "error" }); }
                finally { setRegSubmitting(false); }
              }}>{regSubmitting ? "Submitting..." : "Submit"}</Bt>
              <Bt v="secondary" onClick={() => setAttRegForm(null)}>Cancel</Bt>
            </div>
          </div>;
        })()}
      </Cd>
      <Cd>
        <Sec icon={History} title={`My Regularization Requests (${myRegs.length})`} />
        {myRegs.length === 0 ? (
          <div className="text-center py-4 text-t3 text-[14px]">No regularization requests yet</div>
        ) : (
          <Tbl cols={[
            { k: "d", l: "Date", r: r => <span className="font-mono text-[14px]">{fmtDMY(r.date)}</span> },
            { k: "r", l: "Reason", r: r => <span className="text-[12px] text-t2">{r.reason}</span> },
            { k: "ci", l: "Req Check-In", r: r => <span className="text-[12px]">{r.requested_check_in ? fmtTime(r.requested_check_in) : "—"}</span> },
            { k: "co", l: "Req Check-Out", r: r => <span className="text-[12px]">{r.requested_check_out ? fmtTime(r.requested_check_out) : "—"}</span> },
            { k: "st", l: "Status", r: r => <div><Bg c={r.status === "Approved" ? K.g : r.status === "Rejected" ? K.r : K.a} dot sm>{r.status}</Bg>{r.needs_senior && <div className="text-[12px] mt-[2px]" style={{ color: K.pu }}>#{r.month_seq} this month · Centre Head / HR Head</div>}<RegSnapshot snap={r.pre_regularization_snapshot} /></div> },
            { k: "sub", l: "Submitted", r: r => <span className="text-[12px] text-t3">{r.created_at ? fmtDMY(r.created_at) : "—"}</span> },
          ]} data={myRegs} />
        )}
      </Cd>
      {photoLightbox}
      {attRecDetModal}{manualAttModal}
    </div>;
  }

  if (attTab === "settings" && can('attendance', 'settings', null, 'view')) {
    const localSett = attSettLocal || sett;
    const setLocalSett = (updater) => setAttSettLocal(p => typeof updater === "function" ? updater(p || sett) : { ...(p || sett), ...updater });
    const saveSett = async () => {
      setSettSaving(true);
      try { await api.put('/api/attendance/settings', localSett); setAttSettings(localSett); notify("Settings saved!", { title: "Saved", variant: "success" }); }
      catch (e) { notify(e.message || "Error saving settings", { title: "Error", variant: "error" }); }
      finally { setSettSaving(false); }
    };
    return <div>
      <Banner subtitle="Shifts & Geo-fences — each shift sets hours, reminder and days off; geo-fences set where people can check in" />
      <TabBar />
      {(() => {
        const P = (f, a = 'view') => can('attendance', 'settings', f, a);
        const none = !P('checkin_reminder') && !P('shifts') && !P('assign_shifts') && !P('geo_fence') && !P('face_verification');
        return none ? <Cd><div className="text-center p-6 text-t3 text-[14px]">You can open this tab, but you don't have access to any of its sections yet. An admin can enable them in Settings → Permissions → Attendance → Shift &amp; Geo-fences.</div></Cd> : null;
      })()}
      {can('attendance', 'settings', 'checkin_reminder', 'view') && (
        <fieldset disabled={!can('attendance', 'settings', 'checkin_reminder', 'edit')} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}><ReminderConfigCard /></fieldset>
      )}
      {(can('attendance', 'settings', 'shifts', 'view') || can('attendance', 'settings', 'assign_shifts', 'view')) && (
        <ShiftsCard canEdit={can('attendance', 'settings', 'shifts', 'edit')} canView={can('attendance', 'settings', 'shifts', 'view')} canAssign={can('attendance', 'settings', 'assign_shifts', 'edit')} canSeeCoverage={can('attendance', 'settings', 'assign_shifts', 'view')} canFlex={can('attendance', 'settings', 'flexible_timing', 'edit')} />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {(can('attendance', 'settings', 'geo_fence', 'view') || can('attendance', 'settings', 'face_verification', 'view')) && <Cd>
          <Sec icon={Settings} title="Check-in Rules" />
          <div className="text-[13px] text-t3 mb-[10px]">
            Hours, late limits, reminders and days off are set per <b className="text-t1">shift</b> above, and each employee&apos;s shift is chosen in their profile. These two switches apply to everyone, whatever their shift.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-[10px]">
            {[{ k: "geo_fence_enabled", p: "geo_fence", l: "Geo-fence Enabled", d: "Check-in only from inside the centre's radius" }, { k: "face_verification_enabled", p: "face_verification", l: "Face Verify Enabled", d: "Selfie must match the profile photo" }].filter(f => can('attendance', 'settings', f.p, 'view')).map(f => <div key={f.k} title={can('attendance', 'settings', f.p, 'edit') ? "" : "You can view this but not change it"} onClick={() => { if (can('attendance', 'settings', f.p, 'edit')) setLocalSett(p => ({ ...p, [f.k]: !p[f.k] })); }} className={`px-3 py-2 rounded-[10px] cursor-pointer border ${localSett[f.k] ? "bg-gb border-gd" : "bg-bg2 border-b0"}`}>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-[3px]" style={{ background: localSett[f.k] ? K.g : K.t3 }} />
                <span className={`text-[13px] font-semibold ${localSett[f.k] ? "text-g" : "text-t3"}`}>{f.l}</span>
              </div>
              <div className="text-[11.5px] text-t3 mt-[3px]">{f.d}</div>
            </div>)}
          </div>
          <Bt icon={CheckCircle} full disabled={settSaving || !(can('attendance', 'settings', 'geo_fence', 'edit') || can('attendance', 'settings', 'face_verification', 'edit'))} onClick={saveSett}>{settSaving ? "Saving..." : "Save"}</Bt>
        </Cd>}
        {can('attendance', 'settings', 'geo_fence', 'view') && <Cd>
          <fieldset disabled={!can('attendance', 'settings', 'geo_fence', 'edit')} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <Sec icon={MapPin} title="Office Geo-fences" />
          {officeList.map((o, oi) => <div key={o.id} className="mb-3 p-[10px] bg-bg2 rounded-[10px] border border-b0">
            <div className="font-bold text-[14px] text-t0 mb-[6px]">{o.name} — {o.city}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-[6px] mb-[6px]">
              <div><div className="text-[12px] text-t3 mb-[2px]">Latitude</div><Inp value={String(o.latitude || "")} onChange={v => setAttGeoFences(arr => (arr.length ? arr : (att.offices || [])).map((x, i) => i === oi ? { ...x, latitude: v } : x))} /></div>
              <div><div className="text-[12px] text-t3 mb-[2px]">Longitude</div><Inp value={String(o.longitude || "")} onChange={v => setAttGeoFences(arr => (arr.length ? arr : (att.offices || [])).map((x, i) => i === oi ? { ...x, longitude: v } : x))} /></div>
              <div><div className="text-[12px] text-t3 mb-[2px]">Radius (m)</div><Inp type="number" value={String(o.radius_meters || 200)} onChange={v => setAttGeoFences(arr => (arr.length ? arr : (att.offices || [])).map((x, i) => i === oi ? { ...x, radius_meters: parseInt(v) || 200 } : x))} /></div>
            </div>
            <Bt s="sm" icon={CheckCircle} disabled={geoSavingId === o.id} onClick={async () => { setGeoSavingId(o.id); try { await api.put(`/api/attendance/geo-fence/${o.id}`, { latitude: o.latitude, longitude: o.longitude, radius_meters: o.radius_meters }); notify("Geo-fence saved!", { title: "Saved", variant: "success" }); } catch (e) { notify(e.message || "Error saving geo-fence", { title: "Error", variant: "error" }); } finally { setGeoSavingId(null); } }}>{geoSavingId === o.id ? "Saving..." : "Save"}</Bt>
          </div>)}
          </fieldset>
        </Cd>}
      </div>
    </div>;
  }

  return <div>
    <Banner />
    <TabBar />
    <div className="text-center p-10 text-t3">Loading...</div>
  </div>;
};

// Bulk Regularization — CH raises, Admin approves. HR Head not involved.
// Backend contract: POST /api/attendance/bulk-regularize (multipart with
// supporting_doc), PUT /:id (Approved/Rejected), GET / (role-scoped list).
// See routes/attendance.js for full rules.
const BulkRegularizationTab = ({ role, can = () => true }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [reviewing, setReviewing] = useState(null); // request row for approve/reject
  const [reviewRemarks, setReviewRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/api/attendance/bulk-regularize')
      .then(d => setRows(Array.isArray(d?.rows) ? d.rows : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const flash = (m, err = false) => { setMsg((err ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3500); };

  const decide = async (status) => {
    if (!reviewing) return;
    if (status === 'Rejected' && !reviewRemarks.trim()) { flash('Remarks required when rejecting.', true); return; }
    setBusy(true);
    try {
      const r = await api.put('/api/attendance/bulk-regularize/' + reviewing.id, { status, review_remarks: reviewRemarks.trim() || null });
      flash(status === 'Approved' ? `Approved. ${r.created_records_count || 0} attendance record(s) created.` : 'Rejected.');
      setReviewing(null); setReviewRemarks('');
      load();
    } catch (e) { flash(e.message || 'Failed', true); }
    finally { setBusy(false); }
  };

  const pending = rows.filter(r => r.status === 'Pending');

  return <div>
    <div className="mb-3 p-3 bg-bg2 border border-b0 rounded-lg text-[14px] text-t2 leading-[1.5]">
      <b className="text-t0">Bulk Regularization.</b> For stretches of missed days that the daily self-serve path (3-day back window, 3/month) can't cover — medical leave, natural calamity, extended field assignment. <b className="text-t0">Only Centre Head raises. Only Admin approves.</b> Employees can't self-serve; HR Head is deliberately out of this loop.
    </div>

    {can('attendance', 'bulk_reg', null, 'edit') && <div className="mb-3">
      <Bt icon={PenLine} onClick={() => setShowForm(true)}>Raise Bulk Regularization</Bt>
    </div>}

    {can('attendance', 'bulk_reg', 'review_bulk_reg', 'edit') && pending.length > 0 && <div className="mb-3 p-3 bg-ab border border-ad rounded-lg text-[14px] text-t0">
      <b>{pending.length} pending</b> — review the queue below.
    </div>}

    {msg && <div className={`mb-3 px-3 py-2 rounded-lg text-[14px] font-semibold ${msg.startsWith('Error') ? 'bg-rb text-r border border-rd' : 'bg-gb text-g border border-gd'}`}>{msg}</div>}

    <Cd>
      <div className="flex items-center justify-between mb-2">
        <Sec icon={PenLine} title={`Bulk Regularization Requests (${rows.length})`} />
        <Bt v="ghost" s="sm" icon={RefreshCw} onClick={load}>Refresh</Bt>
      </div>
      {loading ? <div className="text-[14px] text-t3 py-4">Loading…</div>
        : rows.length === 0 ? <div className="text-[14px] text-t3 py-4 text-center">No bulk regularization requests yet.</div>
          : <Tbl cols={[
            { k: 'emp', l: 'Employee', r: r => <span className="text-[14px] font-semibold text-t0">{r.employee_name || r.employee_id}<span className="text-[12px] text-t3 ml-1">({r.employee_id})</span></span> },
            { k: 'rng', l: 'Date range', r: r => <span className="text-[14px] text-t1">{fmtDMY(r.date_from)} → {fmtDMY(r.date_to)}<span className="text-[12px] text-t3 ml-1">({Math.floor((new Date(r.date_to) - new Date(r.date_from)) / 86400000) + 1}d)</span></span> },
            { k: 'st', l: 'Status', r: r => <Bg c={r.status === 'Approved' ? K.g : r.status === 'Rejected' ? K.r : K.a} sm dot>{r.status}</Bg> },
            { k: 'rby', l: 'Raised by', r: r => <span className="text-[14px] text-t1">{r.raised_by_name || r.raised_by}</span> },
            { k: 'rsn', l: 'Reason', r: r => <span className="text-[14px] text-t2 line-clamp-2 max-w-[280px] block">{r.reason}</span> },
            {
              k: 'doc', l: 'Doc', r: r => r.supporting_doc_path
                ? <a href={`${BASE}/${r.supporting_doc_path}`} target="_blank" rel="noreferrer" className="text-p text-[14px] font-semibold no-underline">View</a>
                : <span className="text-[12px] text-t3">—</span>
            },
            {
              k: 'act', l: '', r: r => can('attendance', 'bulk_reg', 'review_bulk_reg', 'edit') && r.status === 'Pending'
                ? <Bt v="ghost" s="xs" icon={Eye} onClick={() => { setReviewing(r); setReviewRemarks(''); }}>Review</Bt>
                : r.status !== 'Pending' && r.reviewed_by_name
                  ? <span className="text-[12px] text-t3">by {r.reviewed_by_name}</span>
                  : null
            },
          ]} data={rows} />}
    </Cd>

    {showForm && <BulkRegularizationForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); flash('Request submitted to Admin.'); }} />}

    {reviewing && (
      <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[200] flex items-center justify-center p-3" onMouseDown={() => !busy && setReviewing(null)}>
        <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[824px] max-h-[85vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[17px] font-extrabold text-t0">Review Bulk Regularization</span>
            <button onClick={() => setReviewing(null)} className="bg-transparent border-0 text-t3 cursor-pointer" disabled={busy}><X size={18} /></button>
          </div>
          <div className="text-[14px] text-t2 mb-2"><b className="text-t0">Employee:</b> {reviewing.employee_name} ({reviewing.employee_id})</div>
          <div className="text-[14px] text-t2 mb-2"><b className="text-t0">Range:</b> {fmtDMY(reviewing.date_from)} → {fmtDMY(reviewing.date_to)} ({Math.floor((new Date(reviewing.date_to) - new Date(reviewing.date_from)) / 86400000) + 1} days) as <b>{reviewing.requested_status}</b></div>
          <div className="text-[14px] text-t2 mb-2"><b className="text-t0">Times:</b> {reviewing.requested_check_in} → {reviewing.requested_check_out}</div>
          <div className="text-[14px] text-t2 mb-2"><b className="text-t0">Raised by:</b> {reviewing.raised_by_name} ({reviewing.raised_by})</div>
          <div className="text-[14px] text-t2 mb-3"><b className="text-t0">Reason:</b><div className="mt-1 p-2 bg-bg2 border border-b0 rounded text-[14px] text-t0 leading-[1.5]">{reviewing.reason}</div></div>
          {reviewing.supporting_doc_path && <div className="mb-3">
            <a href={`${BASE}/${reviewing.supporting_doc_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[14px] text-p font-semibold no-underline"><Eye size={12} /> Open supporting document</a>
          </div>}
          <div className="mb-3">
            <div className="text-[14px] text-t3 mb-[3px]">Review remarks (required to reject)</div>
            <textarea value={reviewRemarks} onChange={e => setReviewRemarks(e.target.value)} rows={3}
              className="w-full p-2 bg-bg2 border border-b0 rounded text-t0 text-[14px] outline-none resize-y"
              placeholder="Optional on Approve; required on Reject." />
          </div>
          <div className="flex gap-2">
            <Bt v="secondary" full onClick={() => setReviewing(null)} disabled={busy}>Cancel</Bt>
            <Bt v="danger" full onClick={() => decide('Rejected')} disabled={busy}>{busy ? '…' : 'Reject'}</Bt>
            <Bt v="success" full onClick={() => decide('Approved')} disabled={busy}>{busy ? '…' : 'Approve'}</Bt>
          </div>
        </div>
      </div>
    )}
  </div>;
};

// CH-facing form to raise a bulk regularization request. FormData
// upload — supporting_doc is mandatory server-side.
const BulkRegularizationForm = ({ onClose, onSaved }) => {
  const [employees, setEmployees] = useState([]);
  const [f, setF] = useState({
    employee_id: '', date_from: '', date_to: '', requested_status: 'Present',
    requested_check_in: '09:30', requested_check_out: '18:30', reason: '',
  });
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    // Employees list — reused across the form; CH's centre-scope is
    // applied at the API layer.
    api.get('/api/employees?limit=1000').then(d => setEmployees(d.data || d || [])).catch(() => setEmployees([]));
  }, []);

  const spanDays = (f.date_from && f.date_to && f.date_to >= f.date_from)
    ? Math.floor((new Date(f.date_to) - new Date(f.date_from)) / 86400000) + 1 : 0;

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!f.employee_id) { setErr('Employee is required.'); return; }
    if (!f.date_from || !f.date_to) { setErr('Both start and end dates are required.'); return; }
    if (f.date_to < f.date_from) { setErr('End date must be on or after start date.'); return; }
    if (spanDays > 60) { setErr(`Range too long — max 60 days (${spanDays}). For longer, use Separation / LWP.`); return; }
    if (f.reason.trim().length < 30) { setErr('Reason must be at least 30 characters — 15+ missed days needs a substantial explanation.'); return; }
    setBusy(true); setErr('');
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (doc) fd.append('supporting_doc', doc);
    try {
      await api.upload('/api/attendance/bulk-regularize', fd);
      onSaved();
    } catch (er) { setErr(er.message || 'Submit failed'); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[200] flex items-center justify-center p-3" onMouseDown={() => !busy && onClose()}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[880px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[17px] font-extrabold text-t0">Raise Bulk Regularization</span>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer" disabled={busy}><X size={18} /></button>
        </div>
        <div className="text-[14px] text-t2 mb-3 leading-[1.5]">Only employees at your centre can be regularized. Range up to 60 days. Admin approves.</div>
        <form onSubmit={submit}>
          <div className="mb-3">
            <div className="text-[14px] text-t3 mb-[3px]">Employee *</div>
            <Sl value={f.employee_id} onChange={v => setF(p => ({ ...p, employee_id: v }))}
              options={[{ v: '', l: '— Select employee —' }, ...employees.map(e => ({ v: e.id || e.employee_id, l: `${e.nm || e.name} (${e.id || e.employee_id})` }))]} />
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <div className="text-[14px] text-t3 mb-[3px]">From date *</div>
              <input type="date" value={f.date_from} onChange={e => setF(p => ({ ...p, date_from: e.target.value }))}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full px-2 py-[7px] bg-bg2 border border-b0 rounded text-t0 text-[14px] outline-none" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-[3px]">To date *</div>
              <input type="date" value={f.date_to} onChange={e => setF(p => ({ ...p, date_to: e.target.value }))}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full px-2 py-[7px] bg-bg2 border border-b0 rounded text-t0 text-[14px] outline-none" />
            </div>
          </div>
          {spanDays > 0 && <div className={`text-[14px] mb-3 ${spanDays > 60 ? 'text-r font-semibold' : 'text-t2'}`}>Range: {spanDays} day{spanDays === 1 ? '' : 's'}{spanDays > 60 ? ' — over 60-day limit' : ''}</div>}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div>
              <div className="text-[14px] text-t3 mb-[3px]">Status</div>
              <Sl value={f.requested_status} onChange={v => setF(p => ({ ...p, requested_status: v }))}
                options={[{ v: 'Present', l: 'Present' }, { v: 'WFH', l: 'WFH' }, { v: 'On Duty', l: 'On Duty' }]} />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-[3px]">Check-in</div>
              <input type="time" value={f.requested_check_in} onChange={e => setF(p => ({ ...p, requested_check_in: e.target.value }))}
                className="w-full px-2 py-[7px] bg-bg2 border border-b0 rounded text-t0 text-[14px] outline-none" />
            </div>
            <div>
              <div className="text-[14px] text-t3 mb-[3px]">Check-out</div>
              <input type="time" value={f.requested_check_out} onChange={e => setF(p => ({ ...p, requested_check_out: e.target.value }))}
                className="w-full px-2 py-[7px] bg-bg2 border border-b0 rounded text-t0 text-[14px] outline-none" />
            </div>
          </div>
          <div className="mb-3">
            <div className="text-[14px] text-t3 mb-[3px]">Reason * <span className="text-t3">({f.reason.trim().length}/30)</span></div>
            <textarea value={f.reason} onChange={e => setF(p => ({ ...p, reason: e.target.value }))} rows={4}
              className="w-full p-2 bg-bg2 border border-b0 rounded text-t0 text-[14px] outline-none resize-y"
              placeholder="What happened + why regularization is warranted. Min 30 characters." />
          </div>
          <div className="mb-3">
            <div className="text-[14px] text-t3 mb-[3px]">Supporting document <span className="text-t3">(optional · PDF or image, max 10 MB)</span></div>
            <input type="file" accept=".pdf,image/*" onChange={e => setDoc(e.target.files?.[0] || null)}
              className="text-[14px] text-t1" />
            {doc && <div className="text-[12px] text-t3 mt-1">{doc.name} · {(doc.size / 1024).toFixed(1)} KB</div>}
          </div>
          {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3">{err}</div>}
          <div className="flex gap-2">
            <Bt v="secondary" full onClick={onClose} disabled={busy}>Cancel</Bt>
            <Bt full onClick={submit} disabled={busy}>{busy ? 'Submitting…' : 'Submit to Admin'}</Bt>
          </div>
        </form>
      </div>
    </div>
  );
};
